/**
 * Correlogram — Scatter-plot matrix with configurable plot types.
 *
 * N x N grid of mini-charts for selected numeric columns:
 *   Diagonal: histogram | box plot | violin/KDE (distribution)
 *   Off-diagonal: scatter | line | hexbin (bivariate) with Pearson r overlay
 *
 * Performance: zoom/pan is done via direct DOM manipulation (no React
 * re-renders). Grid cells are React.memo'd. Tooltips are only in the
 * enlarged modal, not in the 144-cell grid.
 */

import {
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import Plot from "react-plotly.js";
import {
  binData,
  pearsonCorrelation,
  partialCorrelation,
  multipleCorrelationR,
  gaussianKde,
  computeBoxPlotStats,
  hexBin2d,
  lttbDownsample,
} from "./useHistogramBins";
import {
  columnInterestingnessWeight,
  filterConstantColumns,
  rankColumnsByCorrelationTo,
  selectCorrelogramColumns,
  scoreAlignedRelationship,
  type PairRelationshipScore,
  type CorrelogramSelectionMode,
} from "./correlationRanking";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { SurfaceCorrelogram } from "./SurfaceCorrelogram";

// ---------------------------------------------------------------------------
// Plot type configuration
// ---------------------------------------------------------------------------

export type OffDiagPlotType = "scatter" | "line" | "hexbin" | "scatter3d";
export type DiagPlotType = "histogram" | "boxplot" | "violin";
export type RelationshipFitMode = "none" | "linear" | "quadratic" | "cubic" | "rbf";
type CorrelationViewMode = "2d" | "3d";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CorrelogramColumnRole = "condition" | "response" | "metadata" | "derived";
export type CorrelogramEvidenceKind = "observed" | "model-conditioned";

export interface CorrelogramColumn {
  key: string;
  header: string;
  role?: CorrelogramColumnRole;
  evidence?: CorrelogramEvidenceKind;
  source?: "observed" | "model" | "corrected" | "derived";
}

export interface CorrelogramPointContext {
  xField: string;
  yField: string;
  zField?: string;
  xLabel?: string;
  yLabel?: string;
  zLabel?: string;
}

interface DataGroup {
  key: string;
  label: string;
  rows: Record<string, unknown>[];
  color: string;
}

interface CorrelogramProps {
  rows: Record<string, unknown>[];
  numericColumns: CorrelogramColumn[];
  groups?: DataGroup[];
  colorByKey?: string;
  sizeByKey?: string;
  symbolByKey?: string;
  /** When set, enables 3D scatter mode: each off-diagonal cell gets a Z dimension. */
  zAxisKey?: string;
  zAxisLabel?: string;
  zValueResolver?: (context: {
    row: Record<string, unknown>;
    rowIndex: number;
    xKey: string;
    yKey: string;
    zKey: string;
  }) => number | null | undefined;
  onPointClick?: (rowIdx: number) => void;
  /** Right-click on a data point — passes row index and native event for context menu positioning. */
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  selectedRowIdx?: number;
  offDiagPlotType?: OffDiagPlotType;
  diagPlotType?: DiagPlotType;
  onOffDiagPlotTypeChange?: (type: OffDiagPlotType) => void;
  onDiagPlotTypeChange?: (type: DiagPlotType) => void;
  maxColumns?: number;
  defaultPruneBoringAxes?: boolean;
  defaultHideWeakCorrelations?: boolean;
  weakCorrelationThreshold?: number;
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

const SCATTER_COLORS = [
  "#818cf8", "#34d399", "#fbbf24", "#f472b6",
  "#60a5fa", "#a78bfa", "#fb923c", "#2dd4bf",
];

const HISTOGRAM_COLOR = "rgba(99, 102, 241, 0.5)";
const HISTOGRAM_STROKE = "#6366f1";

const GRADIENT_COOL: [number, number, number] = [99, 102, 241];
const GRADIENT_WARM: [number, number, number] = [251, 191, 36];

function gradientColor(t: number): string {
  const r = Math.round(GRADIENT_COOL[0] + t * (GRADIENT_WARM[0] - GRADIENT_COOL[0]));
  const g = Math.round(GRADIENT_COOL[1] + t * (GRADIENT_WARM[1] - GRADIENT_COOL[1]));
  const b = Math.round(GRADIENT_COOL[2] + t * (GRADIENT_WARM[2] - GRADIENT_COOL[2]));
  return `rgb(${r},${g},${b})`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractNumbers(rows: Record<string, unknown>[], key: string): number[] {
  return rows
    .map(r => r[key])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

function extractPairs(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
): { x: number; y: number; idx: number }[] {
  const result: { x: number; y: number; idx: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const xv = row[xKey];
    const yv = row[yKey];
    if (typeof xv === "number" && Number.isFinite(xv) &&
        typeof yv === "number" && Number.isFinite(yv)) {
      result.push({ x: xv, y: yv, idx: i });
    }
  }
  return result;
}

function extractTriples(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): { x: number; y: number; z: number; idx: number }[] {
  const result: { x: number; y: number; z: number; idx: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const xv = row[xKey], yv = row[yKey], zv = row[zKey];
    if (typeof xv === "number" && Number.isFinite(xv) &&
        typeof yv === "number" && Number.isFinite(yv) &&
        typeof zv === "number" && Number.isFinite(zv)) {
      result.push({ x: xv, y: yv, z: zv, idx: i });
    }
  }
  return result;
}

interface ResponseDriver {
  column: CorrelogramColumn;
  r: number;
  score: number;
  count: number;
  evidence: CorrelogramEvidenceKind;
}

interface ResponseDriverSummary {
  text: string;
  title: string;
  hasModelConditioned: boolean;
}

function correlationEvidenceKind(column: CorrelogramColumn): CorrelogramEvidenceKind {
  if (column.evidence) return column.evidence;
  if (column.source === "model" || column.source === "corrected") return "model-conditioned";
  const haystack = `${column.key} ${column.header}`.toLowerCase();
  if (/(model|surrogate|corrected|transport|prior|prediction|predicted)/.test(haystack)) {
    return "model-conditioned";
  }
  return "observed";
}

function formatDriverList(drivers: ResponseDriver[]): string {
  return drivers
    .map((driver) => `${driver.column.header}${driver.evidence === "model-conditioned" ? " (model)" : ""}`)
    .join("/");
}

function summarizeResponseDrivers(
  rows: Record<string, unknown>[],
  columns: CorrelogramColumn[],
  zAxisKey?: string,
  zAxisLabel?: string,
): ResponseDriverSummary | null {
  if (!zAxisKey) return null;
  const drivers: ResponseDriver[] = [];
  for (const column of columns) {
    if (column.key === zAxisKey) continue;
    const pairs = extractPairs(rows, column.key, zAxisKey);
    if (pairs.length < 3) continue;
    const r = pearsonCorrelation(pairs.map((pair) => pair.x), pairs.map((pair) => pair.y));
    if (!Number.isFinite(r)) continue;
    drivers.push({
      column,
      r,
      score: Math.abs(r),
      count: pairs.length,
      evidence: correlationEvidenceKind(column),
    });
  }
  drivers.sort((a, b) => b.score - a.score || b.count - a.count);
  if (drivers.length === 0) {
    return {
      text: `${zAxisLabel ?? zAxisKey} has insufficient overlapping evidence in the selected columns.`,
      title: "No selected column has at least three overlapping finite values with the active response.",
      hasModelConditioned: false,
    };
  }

  const primary = drivers.filter((driver) => driver.score >= 0.65).slice(0, 3);
  const secondary = drivers.filter((driver) => driver.score >= 0.35 && driver.score < 0.65).slice(0, 3);
  const weak = drivers.filter((driver) => driver.score >= 0.15 && driver.score < 0.35).slice(0, 3);
  const pieces: string[] = [];
  if (primary.length) pieces.push(`primarily ${formatDriverList(primary)}`);
  if (secondary.length) pieces.push(`secondary ${formatDriverList(secondary)}`);
  if (weak.length) pieces.push(`weak ${formatDriverList(weak)} coupling`);
  if (pieces.length === 0) pieces.push("no strong linear driver in the selected columns");
  const top = drivers.slice(0, 6);
  const hasModelConditioned = top.some((driver) => driver.evidence === "model-conditioned");
  const observedTop = top.filter((driver) => driver.evidence === "observed");
  const evidenceNote = observedTop.length === 0
    ? "; insufficient direct observed evidence"
    : hasModelConditioned
    ? "; model-conditioned correlations marked"
    : "";
  return {
    text: `${zAxisLabel ?? zAxisKey} is ${pieces.join(", ")}${evidenceNote}.`,
    title: top
      .map((driver) => `${driver.column.header}: r=${driver.r.toFixed(3)}, n=${driver.count.toLocaleString()}, ${driver.evidence}`)
      .join("\n"),
    hasModelConditioned,
  };
}

function shortLabel(val: number): string {
  if (Math.abs(val) >= 10000 || (Math.abs(val) < 0.01 && val !== 0)) {
    return val.toExponential(1);
  }
  if (Number.isInteger(val)) return val.toLocaleString();
  return val.toFixed(2);
}

function rColor(r: number): string {
  const abs = Math.abs(r);
  if (abs > 0.7) return "#34d399";
  if (abs > 0.4) return "#fbbf24";
  return "#6b7280";
}

/** Color for partial correlation badge — blue means z explains the coupling, green means axes are independently coupled. */
function partialRColor(r: number, rPartial: number): string {
  const abs = Math.abs(rPartial);
  const drop = Math.abs(r) - abs;
  // Large drop from r to r_partial → z explains the coupling (blue)
  if (drop > 0.3) return "#60a5fa";
  // Still strongly correlated after controlling for z → independent coupling (green)
  if (abs > 0.5) return "#34d399";
  return "#6b7280";
}

interface ColRange { min: number; max: number }

function computeRange(values: number[]): ColRange | null {
  if (values.length === 0) return null;
  let min = Infinity, max = -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

function solveLinearSystem(matrix: number[][], rhs: number[]): number[] | null {
  const n = rhs.length;
  if (n === 0 || matrix.length !== n || matrix.some(row => row.length !== n)) return null;
  const aug = matrix.map((row, i) => [...row, rhs[i]!]);

  for (let pivot = 0; pivot < n; pivot++) {
    let best = pivot;
    for (let row = pivot + 1; row < n; row++) {
      if (Math.abs(aug[row]![pivot]!) > Math.abs(aug[best]![pivot]!)) best = row;
    }
    if (Math.abs(aug[best]![pivot]!) < 1e-12) return null;
    if (best !== pivot) [aug[pivot], aug[best]] = [aug[best]!, aug[pivot]!];

    const pivotRow = aug[pivot]!;
    const divisor = pivotRow[pivot]!;
    for (let col = pivot; col <= n; col++) pivotRow[col] = pivotRow[col]! / divisor;

    for (let row = 0; row < n; row++) {
      if (row === pivot) continue;
      const target = aug[row]!;
      const factor = target[pivot]!;
      for (let col = pivot; col <= n; col++) {
        target[col] = target[col]! - factor * pivotRow[col]!;
      }
    }
  }

  return aug.map(row => row[n]!);
}

function leastSquaresCoefficients(basisRows: number[][], y: number[]): number[] | null {
  const terms = basisRows[0]?.length ?? 0;
  if (basisRows.length === 0 || terms === 0 || y.length !== basisRows.length) return null;

  const normal = Array.from({ length: terms }, () => Array.from({ length: terms }, () => 0));
  const rhs = Array.from({ length: terms }, () => 0);
  for (let row = 0; row < basisRows.length; row++) {
    const basis = basisRows[row]!;
    for (let i = 0; i < terms; i++) {
      rhs[i] = rhs[i]! + basis[i]! * y[row]!;
      for (let j = 0; j < terms; j++) normal[i]![j] = normal[i]![j]! + basis[i]! * basis[j]!;
    }
  }
  return solveLinearSystem(normal, rhs);
}

function rSquared(actual: number[], predicted: number[]): number {
  if (actual.length === 0 || actual.length !== predicted.length) return 0;
  const mean = actual.reduce((sum, value) => sum + value, 0) / actual.length;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < actual.length; i++) {
    ssRes += (actual[i]! - predicted[i]!) ** 2;
    ssTot += (actual[i]! - mean) ** 2;
  }
  return ssTot > 0 ? Math.max(0, Math.min(1, 1 - ssRes / ssTot)) : 0;
}

function polynomialBasis(x: number, order: 1 | 2 | 3): number[] {
  if (order === 1) return [1, x];
  if (order === 2) return [1, x, x * x];
  return [1, x, x * x, x * x * x];
}

interface FitTraceResult {
  trace: Partial<Plotly.PlotData>;
  label: string;
  scoreLabel?: string;
}

function polynomialFitTrace(
  data: { x: number; y: number; idx: number }[],
  order: 1 | 2 | 3,
): FitTraceResult | null {
  if (data.length < order + 2) return null;
  let xMin = Infinity;
  let xMax = -Infinity;
  let xSum = 0;
  for (const pt of data) {
    xMin = Math.min(xMin, pt.x);
    xMax = Math.max(xMax, pt.x);
    xSum += pt.x;
  }
  const xMean = xSum / data.length;
  const xScale = Math.max(1e-12, (xMax - xMin) / 2);
  const basisRows = data.map(pt => polynomialBasis((pt.x - xMean) / xScale, order));
  const coeffs = leastSquaresCoefficients(basisRows, data.map(pt => pt.y));
  if (!coeffs) return null;

  const predict = (x: number) => {
    const basis = polynomialBasis((x - xMean) / xScale, order);
    return basis.reduce((sum, value, i) => sum + value * coeffs[i]!, 0);
  };
  const samples = 120;
  const xs = Array.from({ length: samples }, (_, i) => xMin + ((xMax - xMin) * i) / (samples - 1 || 1));
  const ys = xs.map(predict);
  const predicted = data.map(pt => predict(pt.x));
  const fitR2 = rSquared(data.map(pt => pt.y), predicted);
  const label = order === 1 ? "Linear fit" : order === 2 ? "Quadratic fit" : "Cubic fit";

  return {
    label,
    scoreLabel: `R2 ${fitR2.toFixed(2)}`,
    trace: {
      x: xs,
      y: ys,
      type: "scatter",
      mode: "lines",
      name: label,
      line: { color: "#fbbf24", width: 3 },
      hovertemplate: `${label}<br>x=%{x}<br>y=%{y}<extra></extra>`,
    },
  };
}

function thinned2D(data: { x: number; y: number; idx: number }[], limit: number): { x: number; y: number; idx: number }[] {
  if (data.length <= limit) return data;
  const stride = Math.ceil(data.length / limit);
  return data.filter((_, index) => index % stride === 0).slice(0, limit);
}

function rbfFitTrace(data: { x: number; y: number; idx: number }[]): FitTraceResult | null {
  if (data.length < 8) return null;
  const train = thinned2D(data, 700);
  let xMin = Infinity;
  let xMax = -Infinity;
  for (const pt of data) {
    xMin = Math.min(xMin, pt.x);
    xMax = Math.max(xMax, pt.x);
  }
  const span = xMax - xMin || 1;
  const bandwidth = span * 0.12 || 1;
  const samples = 140;
  const xs = Array.from({ length: samples }, (_, i) => xMin + (span * i) / (samples - 1 || 1));
  const ys = xs.map((x) => {
    let num = 0;
    let den = 0;
    for (const pt of train) {
      const u = (pt.x - x) / bandwidth;
      const w = Math.exp(-0.5 * u * u);
      num += w * pt.y;
      den += w;
    }
    return den > 1e-9 ? num / den : train[0]!.y;
  });
  return {
    label: "Local RBF fit",
    scoreLabel: `${train.length}/${data.length} pts`,
    trace: {
      x: xs,
      y: ys,
      type: "scatter",
      mode: "lines",
      name: "Local RBF fit",
      line: { color: "#2dd4bf", width: 3, dash: "solid" },
      hovertemplate: "Local RBF fit<br>x=%{x}<br>y=%{y}<extra></extra>",
    },
  };
}

function fitTraceForMode(
  data: { x: number; y: number; idx: number }[],
  fitMode: RelationshipFitMode,
): FitTraceResult | null {
  if (fitMode === "linear") return polynomialFitTrace(data, 1);
  if (fitMode === "quadratic") return polynomialFitTrace(data, 2);
  if (fitMode === "cubic") return polynomialFitTrace(data, 3);
  if (fitMode === "rbf") return rbfFitTrace(data);
  return null;
}

function surfaceBasis(x: number, z: number, order: 1 | 2 | 3): number[] {
  if (order === 1) return [1, x, z];
  if (order === 2) return [1, x, z, x * x, x * z, z * z];
  return [1, x, z, x * x, x * z, z * z, x ** 3, x * x * z, x * z * z, z ** 3];
}

function polynomialSurfaceTrace(
  data: { x: number; y: number; z: number; idx: number }[],
  order: 1 | 2 | 3,
): FitTraceResult | null {
  const minPoints = order === 1 ? 5 : order === 2 ? 9 : 14;
  if (data.length < minPoints) return null;
  let xMin = Infinity, xMax = -Infinity, xSum = 0;
  let zMin = Infinity, zMax = -Infinity, zSum = 0;
  for (const pt of data) {
    xMin = Math.min(xMin, pt.x); xMax = Math.max(xMax, pt.x); xSum += pt.x;
    zMin = Math.min(zMin, pt.z); zMax = Math.max(zMax, pt.z); zSum += pt.z;
  }
  const xMean = xSum / data.length;
  const zMean = zSum / data.length;
  const xScale = Math.max(1e-12, (xMax - xMin) / 2);
  const zScale = Math.max(1e-12, (zMax - zMin) / 2);
  const normX = (x: number) => (x - xMean) / xScale;
  const normZ = (z: number) => (z - zMean) / zScale;
  const basisRows = data.map(pt => surfaceBasis(normX(pt.x), normZ(pt.z), order));
  const coeffs = leastSquaresCoefficients(basisRows, data.map(pt => pt.y));
  if (!coeffs) return null;

  const predict = (x: number, z: number) => {
    const basis = surfaceBasis(normX(x), normZ(z), order);
    return basis.reduce((sum, value, i) => sum + value * coeffs[i]!, 0);
  };
  const grid = 24;
  const xGrid: number[][] = [];
  const yGrid: number[][] = [];
  const zGrid: number[][] = [];
  for (let zi = 0; zi < grid; zi++) {
    const z = zMin + ((zMax - zMin) * zi) / (grid - 1 || 1);
    const xRow: number[] = [];
    const yRow: number[] = [];
    const zRow: number[] = [];
    for (let xi = 0; xi < grid; xi++) {
      const x = xMin + ((xMax - xMin) * xi) / (grid - 1 || 1);
      xRow.push(x);
      yRow.push(predict(x, z));
      zRow.push(z);
    }
    xGrid.push(xRow);
    yGrid.push(yRow);
    zGrid.push(zRow);
  }
  const predicted = data.map(pt => predict(pt.x, pt.z));
  const fitR2 = rSquared(data.map(pt => pt.y), predicted);
  const label = order === 1 ? "Linear surface" : order === 2 ? "Quadratic surface" : "Cubic surface";
  return {
    label,
    scoreLabel: `R2 ${fitR2.toFixed(2)}`,
    trace: {
      x: xGrid,
      y: yGrid,
      z: zGrid,
      type: "surface",
      name: label,
      opacity: 0.38,
      colorscale: "Viridis",
      showscale: false,
      hovertemplate: `${label}<extra></extra>`,
    } as unknown as Partial<Plotly.PlotData>,
  };
}

function thinned3D(data: { x: number; y: number; z: number; idx: number }[], limit: number): { x: number; y: number; z: number; idx: number }[] {
  if (data.length <= limit) return data;
  const stride = Math.ceil(data.length / limit);
  return data.filter((_, index) => index % stride === 0).slice(0, limit);
}

function rbfSurfaceTrace(data: { x: number; y: number; z: number; idx: number }[]): FitTraceResult | null {
  if (data.length < 12) return null;
  const train = thinned3D(data, 600);
  let xMin = Infinity, xMax = -Infinity;
  let zMin = Infinity, zMax = -Infinity;
  for (const pt of data) {
    xMin = Math.min(xMin, pt.x); xMax = Math.max(xMax, pt.x);
    zMin = Math.min(zMin, pt.z); zMax = Math.max(zMax, pt.z);
  }
  const xSpan = xMax - xMin || 1;
  const zSpan = zMax - zMin || 1;
  const bandwidth = 0.18;
  const grid = 24;
  const xGrid: number[][] = [];
  const yGrid: number[][] = [];
  const zGrid: number[][] = [];
  for (let zi = 0; zi < grid; zi++) {
    const z = zMin + (zSpan * zi) / (grid - 1 || 1);
    const xRow: number[] = [];
    const yRow: number[] = [];
    const zRow: number[] = [];
    for (let xi = 0; xi < grid; xi++) {
      const x = xMin + (xSpan * xi) / (grid - 1 || 1);
      let num = 0;
      let den = 0;
      let nearest = train[0]!;
      let nearestDist = Infinity;
      for (const pt of train) {
        const dx = (pt.x - x) / xSpan;
        const dz = (pt.z - z) / zSpan;
        const dist = dx * dx + dz * dz;
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = pt;
        }
        const w = Math.exp(-0.5 * dist / (bandwidth * bandwidth));
        num += w * pt.y;
        den += w;
      }
      xRow.push(x);
      yRow.push(den > 1e-9 ? num / den : nearest.y);
      zRow.push(z);
    }
    xGrid.push(xRow);
    yGrid.push(yRow);
    zGrid.push(zRow);
  }
  return {
    label: "Local RBF surface",
    scoreLabel: `${train.length}/${data.length} pts`,
    trace: {
      x: xGrid,
      y: yGrid,
      z: zGrid,
      type: "surface",
      name: "Local RBF surface",
      opacity: 0.42,
      colorscale: "Turbo",
      showscale: false,
      hovertemplate: "Local RBF surface<extra></extra>",
    } as unknown as Partial<Plotly.PlotData>,
  };
}

function surfaceTraceForMode(
  data: { x: number; y: number; z: number; idx: number }[],
  fitMode: RelationshipFitMode,
): FitTraceResult | null {
  if (fitMode === "linear") return polynomialSurfaceTrace(data, 1);
  if (fitMode === "quadratic") return polynomialSurfaceTrace(data, 2);
  if (fitMode === "cubic") return polynomialSurfaceTrace(data, 3);
  if (fitMode === "rbf") return rbfSurfaceTrace(data);
  return null;
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const LABEL_COL_W = 64;
const HEADER_ROW_H = 18;
const TICK_ROW_H = 18;
const CELL_MARGIN = { top: 4, right: 4, bottom: 4, left: 4 };
const ENLARGED_MARGIN = { top: 24, right: 16, bottom: 32, left: 48 };
const MAX_AUTO_COLUMNS = 12;

const SELECTION_MODES: Array<{ key: CorrelogramSelectionMode; label: string }> = [
  { key: "interesting", label: "Interesting" },
  { key: "random", label: "Random" },
  { key: "outliers", label: "Outliers" },
  { key: "linear", label: "Linear" },
  { key: "quadratic", label: "Quadratic" },
  { key: "cubic", label: "Cubic" },
  { key: "exponential", label: "Exponential" },
];

const FIT_MODES: Array<{ key: RelationshipFitMode; label: string; short: string }> = [
  { key: "none", label: "None", short: "None" },
  { key: "linear", label: "Linear fit", short: "Linear" },
  { key: "quadratic", label: "Quadratic fit", short: "Quad" },
  { key: "cubic", label: "Cubic fit", short: "Cubic" },
  { key: "rbf", label: "Local RBF surface", short: "RBF" },
];

// ---------------------------------------------------------------------------
// useZoomPan — DOM-only, zero React re-renders during modified zoom/pan
// ---------------------------------------------------------------------------

function useZoomPan(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  innerRef: React.RefObject<HTMLDivElement | null>,
  resetBtnRef: React.RefObject<HTMLButtonElement | null>,
) {
  const state = useRef({ scale: 1, tx: 0, ty: 0 });
  const ctrlPressed = useRef(false);
  const metaPressed = useRef(false);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const applyTransform = useCallback(() => {
    const { scale, tx, ty } = state.current;
    if (innerRef.current) {
      innerRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    }
    if (resetBtnRef.current) {
      resetBtnRef.current.style.display =
        (scale !== 1 || tx !== 0 || ty !== 0) ? "block" : "none";
    }
  }, [innerRef, resetBtnRef]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!ctrlPressed.current && !metaPressed.current) return;
      e.preventDefault();
      const s = state.current;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      // Physical Ctrl/Cmd+wheel zooms; ordinary wheel/trackpad movement
      // remains native scrolling so tall matrices can be inspected normally.
      const magnitude = 0.08;
      const factor = e.deltaY > 0 ? (1 - magnitude) : (1 + magnitude);
      const next = Math.max(0.25, Math.min(5, s.scale * factor));
      const r = next / s.scale;
      s.tx = cx - r * (cx - s.tx);
      s.ty = cy - r * (cy - s.ty);
      s.scale = next;
      applyTransform();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Control") ctrlPressed.current = true;
      if (e.key === "Meta") metaPressed.current = true;
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Control") ctrlPressed.current = false;
      if (e.key === "Meta") metaPressed.current = false;
    };

    const onWindowBlur = () => {
      ctrlPressed.current = false;
      metaPressed.current = false;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!ctrlPressed.current && !metaPressed.current) return;
      dragging.current = true;
      didDrag.current = false;
      last.current = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDrag.current = true;
      last.current = { x: e.clientX, y: e.clientY };
      state.current.tx += dx;
      state.current.ty += dy;
      applyTransform();
    };

    const onPointerUp = () => { dragging.current = false; };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [viewportRef, applyTransform]);

  const resetTransform = useCallback(() => {
    state.current = { scale: 1, tx: 0, ty: 0 };
    applyTransform();
  }, [applyTransform]);

  const reset = useCallback(() => {
    resetTransform();
  }, [resetTransform]);

  const wasDrag = useCallback(() => didDrag.current, []);

  return { reset, wasDrag, resetTransform };
}

// ---------------------------------------------------------------------------
// Memoized sub-components (grid cells — no tooltips for perf)
// ---------------------------------------------------------------------------

const HistogramCellGrid = memo(function HistogramCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
  label?: string;
}) {
  const bins = useMemo(() => binData(values), [values]);

  if (bins.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      <BarChart width={size} height={size} data={bins} margin={CELL_MARGIN}>
        <XAxis dataKey="mid" hide />
        <YAxis hide width={0} />
        <Bar dataKey="count" fill={HISTOGRAM_COLOR} stroke={HISTOGRAM_STROKE} strokeWidth={1} radius={[2, 2, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Box plot cell (diagonal)
// ---------------------------------------------------------------------------

const BOX_FILL = "rgba(99, 102, 241, 0.35)";
const BOX_STROKE = "#6366f1";
const MEDIAN_COLOR = "#fbbf24";
const OUTLIER_COLOR = "#f472b6";

const BoxPlotCellGrid = memo(function BoxPlotCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
}) {
  const stats = useMemo(() => computeBoxPlotStats(values), [values]);
  if (!stats) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { whiskerLow, whiskerHigh, q1, q3, median, outliers } = stats;
  const range = whiskerHigh - whiskerLow || 1;

  const toY = (v: number) => m.top + plotH - ((v - whiskerLow) / range) * plotH;
  const cx = m.left + plotW / 2;
  const boxW = plotW * 0.5;

  return (
    <svg width={size} height={size}>
      {/* Whisker line */}
      <line x1={cx} y1={toY(whiskerLow)} x2={cx} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1} />
      {/* Whisker caps */}
      <line x1={cx - boxW * 0.3} y1={toY(whiskerLow)} x2={cx + boxW * 0.3} y2={toY(whiskerLow)} stroke={BOX_STROKE} strokeWidth={1} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerHigh)} x2={cx + boxW * 0.3} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1} />
      {/* Box */}
      <rect x={cx - boxW / 2} y={toY(q3)} width={boxW} height={toY(q1) - toY(q3)} fill={BOX_FILL} stroke={BOX_STROKE} strokeWidth={1} rx={2} />
      {/* Median */}
      <line x1={cx - boxW / 2} y1={toY(median)} x2={cx + boxW / 2} y2={toY(median)} stroke={MEDIAN_COLOR} strokeWidth={2} />
      {/* Outliers */}
      {outliers.map((v, i) => (
        <circle key={i} cx={cx} cy={toY(v)} r={2} fill={OUTLIER_COLOR} fillOpacity={0.7} />
      ))}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// Violin/KDE cell (diagonal)
// ---------------------------------------------------------------------------

const VIOLIN_FILL = "rgba(99, 102, 241, 0.3)";
const VIOLIN_STROKE = "#818cf8";

const ViolinCellGrid = memo(function ViolinCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
}) {
  const kdeData = useMemo(() => gaussianKde(values, 32), [values]);

  if (kdeData.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <div style={{ width: size, height: size }}>
      <AreaChart width={size} height={size} data={kdeData} margin={CELL_MARGIN}>
        <XAxis dataKey="value" hide />
        <YAxis hide width={0} />
        <Area type="monotone" dataKey="density" fill={VIOLIN_FILL} stroke={VIOLIN_STROKE} strokeWidth={1.5} isAnimationActive={false} />
      </AreaChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Line plot cell (off-diagonal)
// ---------------------------------------------------------------------------

const LINE_COLOR = "#818cf8";

const LineCellGrid = memo(function LineCellGrid({
  data,
  size,
  colorByValues,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  colorByValues?: string[];
  groupColorMap?: Map<string, string>;
}) {
  // Split data into separate lines per group
  const groupedLines = useMemo(() => {
    if (!colorByValues || !groupColorMap || groupColorMap.size <= 1) {
      return [{ data: [...data].sort((a, b) => a.x - b.x), color: LINE_COLOR }];
    }
    const byGroup = new Map<string, { x: number; y: number; idx: number }[]>();
    for (const pt of data) {
      const group = colorByValues[pt.idx] ?? "";
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group)!.push(pt);
    }
    return [...byGroup.entries()].map(([key, pts]) => ({
      data: pts.sort((a, b) => a.x - b.x),
      color: groupColorMap.get(key) ?? LINE_COLOR,
    }));
  }, [data, colorByValues, groupColorMap]);

  const domain = useMemo(() => {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    }
    return { x: [xMin, xMax] as [number, number], y: [yMin, yMax] as [number, number] };
  }, [data]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  // Render using SVG for multi-line support
  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const xSpan = domain.x[1] - domain.x[0] || 1;
  const ySpan = domain.y[1] - domain.y[0] || 1;

  const toX = (v: number) => m.left + ((v - domain.x[0]) / xSpan) * plotW;
  const toY = (v: number) => m.top + plotH - ((v - domain.y[0]) / ySpan) * plotH;

  return (
    <svg width={size} height={size}>
      {groupedLines.map((line, gi) => {
        if (line.data.length < 2) return null;
        const pathD = line.data
          .map((pt, i) => `${i === 0 ? "M" : "L"}${toX(pt.x).toFixed(1)},${toY(pt.y).toFixed(1)}`)
          .join(" ");
        return (
          <path
            key={gi}
            d={pathD}
            fill="none"
            stroke={line.color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// Hexbin / heatmap cell (off-diagonal)
// ---------------------------------------------------------------------------

const HEXBIN_PALETTE = [
  "rgba(99, 102, 241, 0.15)",
  "rgba(99, 102, 241, 0.3)",
  "rgba(99, 102, 241, 0.5)",
  "rgba(129, 140, 248, 0.6)",
  "rgba(251, 191, 36, 0.6)",
  "rgba(251, 191, 36, 0.8)",
  "rgba(244, 114, 182, 0.8)",
  "rgba(244, 114, 182, 1)",
];

function hexColor(count: number, maxCount: number): string {
  if (maxCount <= 0) return HEXBIN_PALETTE[0]!;
  const t = count / maxCount;
  const idx = Math.min(Math.floor(t * HEXBIN_PALETTE.length), HEXBIN_PALETTE.length - 1);
  return HEXBIN_PALETTE[idx]!;
}

const HexbinCellGrid = memo(function HexbinCellGrid({
  data,
  size,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
}) {
  const hexData = useMemo(() => {
    if (data.length === 0) return { bins: [], maxCount: 0, xRange: [0, 1] as [number, number], yRange: [0, 1] as [number, number] };
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const d of data) {
      if (d.x < xMin) xMin = d.x;
      if (d.x > xMax) xMax = d.x;
      if (d.y < yMin) yMin = d.y;
      if (d.y > yMax) yMax = d.y;
    }
    const xRange: [number, number] = [xMin, xMax];
    const yRange: [number, number] = [yMin, yMax];
    const gridSize = Math.max(6, Math.min(16, Math.ceil(Math.sqrt(data.length / 3))));
    const bins = hexBin2d(data, xRange, yRange, gridSize);
    const maxCount = Math.max(1, ...bins.map(b => b.count));
    return { bins, maxCount, xRange, yRange };
  }, [data]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { bins, maxCount, xRange, yRange } = hexData;
  const xSpan = xRange[1] - xRange[0] || 1;
  const ySpan = yRange[1] - yRange[0] || 1;
  const gridSize = Math.max(6, Math.min(16, Math.ceil(Math.sqrt(data.length / 3))));
  const rectW = Math.max(2, plotW / gridSize);
  const rectH = Math.max(2, plotH / gridSize);

  return (
    <svg width={size} height={size}>
      {bins.map((bin, i) => {
        const px = m.left + ((bin.cx - xRange[0]) / xSpan) * plotW;
        const py = m.top + plotH - ((bin.cy - yRange[0]) / ySpan) * plotH;
        return (
          <rect
            key={i}
            x={px - rectW / 2}
            y={py - rectH / 2}
            width={rectW}
            height={rectH}
            rx={2}
            fill={hexColor(bin.count, maxCount)}
          />
        );
      })}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// 3D Scatter mini-cell — lightweight SVG projection with z encoded as color
//
// Using Plotly WebGL for every grid cell exhausts the browser's WebGL context
// limit (~8-16 contexts). Instead we render a lightweight pseudo-3D SVG
// projection that can be tilted/panned. The full
// interactive 3D Plotly view is reserved for the expanded modal on click.
// ---------------------------------------------------------------------------

const Z_GRADIENT_COOL: [number, number, number] = [59, 130, 246];   // blue-400
const Z_GRADIENT_WARM: [number, number, number] = [239, 68, 68];    // red-400
const MINI_3D_MAX_POINTS = 800;
const MINI_3D_DRAG_POINTS = 260;

interface Mini3DCamera {
  yaw: number;
  pitch: number;
  panX: number;
  panY: number;
  zoom: number;
}

const DEFAULT_MINI_3D_CAMERA: Mini3DCamera = {
  yaw: -0.62,
  pitch: 0.46,
  panX: 0,
  panY: 0,
  zoom: 1,
};

function zGradientColor(t: number): string {
  const r = Math.round(Z_GRADIENT_COOL[0] + t * (Z_GRADIENT_WARM[0] - Z_GRADIENT_COOL[0]));
  const g = Math.round(Z_GRADIENT_COOL[1] + t * (Z_GRADIENT_WARM[1] - Z_GRADIENT_COOL[1]));
  const b = Math.round(Z_GRADIENT_COOL[2] + t * (Z_GRADIENT_WARM[2] - Z_GRADIENT_COOL[2]));
  return `rgb(${r},${g},${b})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function projectMini3DPoint(
  x: number,
  y: number,
  z: number,
  camera: Mini3DCamera,
  centerX: number,
  centerY: number,
  scale: number,
): { x: number; y: number; depth: number } {
  const cy = Math.cos(camera.yaw);
  const sy = Math.sin(camera.yaw);
  const cp = Math.cos(camera.pitch);
  const sp = Math.sin(camera.pitch);
  const x1 = x * cy + z * sy;
  const z1 = -x * sy + z * cy;
  const y1 = y * cp - z1 * sp;
  const z2 = y * sp + z1 * cp;
  return {
    x: centerX + x1 * scale,
    y: centerY - y1 * scale,
    depth: z2,
  };
}

const Scatter3DCellGrid = memo(function Scatter3DCellGrid({
  data,
  size,
  camera = DEFAULT_MINI_3D_CAMERA,
  syncCamera = true,
  onCameraChange,
}: {
  data: { x: number; y: number; z: number; idx: number }[];
  size: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
  camera?: Mini3DCamera;
  syncCamera?: boolean;
  onCameraChange?: (camera: Mini3DCamera) => void;
}) {
  const [localCamera, setLocalCamera] = useState(camera);
  const [dragCamera, setDragCamera] = useState<Mini3DCamera | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const prevSyncRef = useRef(syncCamera);
  const frameRef = useRef<number | null>(null);
  const pendingCameraRef = useRef<Mini3DCamera | null>(null);
  const finalDragCameraRef = useRef<Mini3DCamera | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    camera: Mini3DCamera;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (prevSyncRef.current && !syncCamera) setLocalCamera(camera);
    prevSyncRef.current = syncCamera;
  }, [camera, syncCamera]);

  useEffect(() => () => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
  }, []);

  const activeCamera = dragCamera ?? (syncCamera ? camera : localCamera);
  const scheduleDragCamera = useCallback((next: Mini3DCamera) => {
    pendingCameraRef.current = next;
    finalDragCameraRef.current = next;
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const pending = pendingCameraRef.current;
      pendingCameraRef.current = null;
      if (pending) setDragCamera(pending);
    });
  }, []);

  const layout = useMemo(() => {
    if (data.length === 0) return null;
    let xMin = Infinity, xMax = -Infinity;
    let yMin = Infinity, yMax = -Infinity;
    let zMin = Infinity, zMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x; if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y; if (pt.y > yMax) yMax = pt.y;
      if (pt.z < zMin) zMin = pt.z; if (pt.z > zMax) zMax = pt.z;
    }
    return {
      xMin, xMax, xSpan: xMax - xMin || 1,
      yMin, yMax, ySpan: yMax - yMin || 1,
      zMin, zSpan: zMax - zMin || 1,
    };
  }, [data]);

  const visibleData = useMemo(() => {
    const limit = isDragging ? MINI_3D_DRAG_POINTS : MINI_3D_MAX_POINTS;
    if (data.length <= limit) return data;
    const step = data.length / limit;
    return Array.from({ length: limit }, (_, i) => data[Math.floor(i * step)]!)
      .filter(Boolean);
  }, [data, isDragging]);

  const projected = useMemo(() => {
    if (!layout) return null;
    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;
    const centerX = m.left + plotW / 2 + activeCamera.panX;
    const centerY = m.top + plotH / 2 + activeCamera.panY;
    const scale = Math.max(8, Math.min(plotW, plotH) * 0.92 * activeCamera.zoom);
    const project = (x: number, y: number, z: number) =>
      projectMini3DPoint(x, y, z, activeCamera, centerX, centerY, scale);
    const points = visibleData.map((pt) => {
      const xNorm = ((pt.x - layout.xMin) / layout.xSpan) - 0.5;
      const yNorm = ((pt.y - layout.yMin) / layout.ySpan) - 0.5;
      const zNorm = ((pt.z - layout.zMin) / layout.zSpan) - 0.5;
      const p = project(xNorm, yNorm, zNorm);
      const t = (pt.z - layout.zMin) / layout.zSpan;
      return {
        key: pt.idx,
        cx: p.x,
        cy: p.y,
        depth: p.depth,
        color: zGradientColor(t),
      };
    }).sort((a, b) => a.depth - b.depth);
    const origin = project(0, 0, 0);
    return {
      points,
      axes: [
        { key: "x", end: project(0.58, 0, 0), color: "rgba(96,165,250,0.78)" },
        { key: "y", end: project(0, 0.58, 0), color: "rgba(52,211,153,0.78)" },
        { key: "z", end: project(0, 0, 0.58), color: "rgba(248,113,113,0.78)" },
      ],
      origin,
    };
  }, [activeCamera, layout, size, visibleData]);

  const handlePointerDown = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    finalDragCameraRef.current = activeCamera;
    pendingCameraRef.current = null;
    setDragCamera(activeCamera);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      camera: activeCamera,
      moved: false,
    };
    setIsDragging(true);
  }, [activeCamera]);

  const handlePointerMove = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) {
      drag.moved = true;
      suppressClickRef.current = true;
    }
    if (!drag.moved) return;
    event.preventDefault();
    if (event.shiftKey) {
      scheduleDragCamera({
        ...drag.camera,
        panX: clamp(drag.camera.panX + dx, -size * 0.35, size * 0.35),
        panY: clamp(drag.camera.panY + dy, -size * 0.35, size * 0.35),
      });
      return;
    }
    scheduleDragCamera({
      ...drag.camera,
      yaw: drag.camera.yaw + dx * 0.014,
      pitch: clamp(drag.camera.pitch - dy * 0.014, -1.25, 1.25),
    });
  }, [scheduleDragCamera, size]);

  const handlePointerEnd = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      const finalCamera = finalDragCameraRef.current;
      dragRef.current = null;
      pendingCameraRef.current = null;
      finalDragCameraRef.current = null;
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (finalCamera) {
        if (syncCamera) onCameraChange?.(finalCamera);
        else setLocalCamera(finalCamera);
      }
      setDragCamera(null);
      setIsDragging(false);
    }
  }, [onCameraChange, syncCamera]);

  const handleClick = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }, []);

  if (!layout || data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const r = Math.max(1.6, Math.min(3.6, 150 / Math.sqrt(visibleData.length)));

  return (
    <svg
      width={size}
      height={size}
      role="img"
      aria-label="3D correlogram cell"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onClick={handleClick}
      style={{ cursor: isDragging ? "grabbing" : "grab", touchAction: "none" }}
    >
      <rect x={0} y={0} width={size} height={size} fill="transparent" />
      {projected?.axes.map((axis) => (
        <line
          key={axis.key}
          x1={projected.origin.x}
          y1={projected.origin.y}
          x2={axis.end.x}
          y2={axis.end.y}
          stroke={axis.color}
          strokeWidth={1.2}
          strokeLinecap="round"
          opacity={0.65}
        />
      ))}
      {projected?.points.map((pt) => (
        <circle
          key={pt.key}
          cx={pt.cx}
          cy={pt.cy}
          r={r}
          fill={pt.color}
          opacity={0.58 + Math.max(0, Math.min(0.3, (pt.depth + 0.7) * 0.18))}
        />
      ))}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// 3D Scatter enlarged modal (interactive Plotly)
// ---------------------------------------------------------------------------

function Scatter3DCellModal({
  data, size, r, rPartial, multiR, xLabel, yLabel, zLabel,
  colorByValues, colorByNumbers, groupColorMap,
  onPointClick, onPointContextMenu, plotContext, selectedRowIdx, fitMode = "none",
}: {
  data: { x: number; y: number; z: number; idx: number }[];
  size: number;
  r: number;
  rPartial?: number;
  multiR?: number;
  xLabel?: string;
  yLabel?: string;
  zLabel?: string;
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
  onPointClick?: (rowIdx: number) => void;
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  plotContext?: CorrelogramPointContext;
  selectedRowIdx?: number;
  fitMode?: RelationshipFitMode;
}) {
  const hoveredIdxRef = useRef<number | null>(null);
  const { trace, selectedTrace, fit } = useMemo(() => {
    if (data.length === 0) return { trace: null, selectedTrace: null, fit: null };
    const colors: string[] = [];
    for (const pt of data) {
      if (groupColorMap && colorByValues) {
        colors.push(groupColorMap.get(colorByValues[pt.idx] ?? "") ?? SCATTER_COLORS[0]!);
      } else if (colorByNumbers) {
        let min = Infinity, max = -Infinity;
        for (const p of data) {
          const v = colorByNumbers[p.idx];
          if (typeof v === "number" && Number.isFinite(v)) { if (v < min) min = v; if (v > max) max = v; }
        }
        const span = max - min || 1;
        const v = colorByNumbers[pt.idx];
        colors.push(typeof v === "number" && Number.isFinite(v) ? gradientColor((v - min) / span) : SCATTER_COLORS[0]!);
      } else {
        colors.push(SCATTER_COLORS[0]!);
      }
    }
    const mainTrace = {
      x: data.map(d => d.x),
      y: data.map(d => d.y),
      z: data.map(d => d.z),
      text: data.map(d => `idx: ${d.idx}`),
      customdata: data.map(d => d.idx),
      type: "scatter3d" as const,
      mode: "markers" as const,
      marker: {
        size: Math.max(2.5, Math.min(5, 300 / data.length)),
        color: colors,
        opacity: 0.7,
      },
      hovertemplate: `${xLabel ?? "x"}: %{x}<br>${yLabel ?? "y"}: %{y}<br>${zLabel ?? "z"}: %{z}<extra></extra>`,
    };
    let selTrace: Partial<Plotly.PlotData> | null = null;
    if (selectedRowIdx != null) {
      const sel = data.find(d => d.idx === selectedRowIdx);
      if (sel) {
        selTrace = {
          x: [sel.x], y: [sel.y], z: [sel.z],
          type: "scatter3d" as const,
          mode: "markers" as const,
          marker: { size: 10, color: SELECTED_FILL, symbol: "diamond", line: { color: SELECTED_STROKE, width: 2 } },
          showlegend: false,
          hoverinfo: "skip" as const,
        };
      }
    }
    return { trace: mainTrace, selectedTrace: selTrace, fit: surfaceTraceForMode(data, fitMode) };
  }, [data, colorByValues, colorByNumbers, groupColorMap, selectedRowIdx, xLabel, yLabel, zLabel, fitMode]);

  if (!trace || data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280" }}>No data</span>
      </div>
    );
  }

  const layout: Partial<Plotly.Layout> = {
    width: size, height: size,
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    margin: { t: 30, r: 10, b: 10, l: 10 },
    scene: {
      xaxis: { title: { text: xLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      yaxis: { title: { text: yLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      zaxis: { title: { text: zLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      bgcolor: "transparent",
    },
    showlegend: false,
    font: { color: "#9ca3af", size: 10 },
  };

  const traces = [
    ...(fit ? [fit.trace] : []),
    trace,
    ...(selectedTrace ? [selectedTrace] : []),
  ];
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onPointContextMenu || hoveredIdxRef.current == null) return;
    event.preventDefault();
    event.stopPropagation();
    onPointContextMenu(hoveredIdxRef.current, event, plotContext);
  };

  return (
    <div style={{ width: size, height: size, position: "relative" }} onContextMenu={handleContextMenu}>
      {/* Correlation stats overlay */}
      <div style={{ position: "absolute", top: 8, right: 16, zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)", textAlign: "right" }}>
        {Number.isFinite(r) && (
          <div style={{ fontSize: 12, fontWeight: 700, color: rColor(r) }}>
            r = {r.toFixed(3)}
          </div>
        )}
        {rPartial != null && Number.isFinite(rPartial) && (
          <div style={{ fontSize: 11, fontWeight: 600, color: partialRColor(r, rPartial) }}>
            r<sub>partial</sub> = {rPartial.toFixed(3)}
          </div>
        )}
        {multiR != null && Number.isFinite(multiR) && (
          <div style={{ fontSize: 11, fontWeight: 600, color: "#a78bfa" }}>
            R = {multiR.toFixed(3)}
          </div>
        )}
        {fit && (
          <div style={{ marginTop: 3, fontSize: 11, fontWeight: 700, color: fitMode === "rbf" ? "#2dd4bf" : "#fbbf24" }}>
            {fit.label}{fit.scoreLabel ? ` · ${fit.scoreLabel}` : ""}
          </div>
        )}
      </div>
      <Plot
        data={traces as unknown as Plotly.Data[]}
        layout={layout as unknown as Plotly.Layout}
        config={{ displayModeBar: true, modeBarButtonsToRemove: ["toImage"], responsive: true }}
        style={{ width: size, height: size }}
        onClick={(event) => {
          if (!onPointClick || !event.points?.[0]) return;
          const pt = event.points[0];
          const idx = (pt as { customdata?: number }).customdata;
          if (typeof idx === "number") onPointClick(idx);
        }}
        onHover={(event) => {
          const pt = event.points?.[0];
          const idx = (pt as { customdata?: number } | undefined)?.customdata;
          hoveredIdxRef.current = typeof idx === "number" ? idx : null;
        }}
        onUnhover={() => {
          hoveredIdxRef.current = null;
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enlarged modal versions of new plot types
// ---------------------------------------------------------------------------

function BoxPlotCellModal({ values, size, label }: { values: number[]; size: number; label: string }): ReactNode {
  const stats = useMemo(() => computeBoxPlotStats(values), [values]);
  if (!stats) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  const m = ENLARGED_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { whiskerLow, whiskerHigh, q1, q3, median, outliers } = stats;
  const range = (whiskerHigh - whiskerLow) || 1;
  const padded = range * 0.1;
  const lo = whiskerLow - padded;
  const hi = whiskerHigh + padded;
  const fullRange = hi - lo || 1;

  const toY = (v: number) => m.top + plotH - ((v - lo) / fullRange) * plotH;
  const cx = m.left + plotW / 2;
  const boxW = plotW * 0.4;

  return (
    <svg width={size} height={size}>
      <text x={size / 2} y={size - 8} textAnchor="middle" fontSize={12} fill="#9ca3af">{label}</text>
      {/* Whisker */}
      <line x1={cx} y1={toY(whiskerLow)} x2={cx} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1.5} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerLow)} x2={cx + boxW * 0.3} y2={toY(whiskerLow)} stroke={BOX_STROKE} strokeWidth={1.5} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerHigh)} x2={cx + boxW * 0.3} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1.5} />
      {/* Box */}
      <rect x={cx - boxW / 2} y={toY(q3)} width={boxW} height={Math.max(1, toY(q1) - toY(q3))} fill={BOX_FILL} stroke={BOX_STROKE} strokeWidth={1.5} rx={3} />
      {/* Median */}
      <line x1={cx - boxW / 2} y1={toY(median)} x2={cx + boxW / 2} y2={toY(median)} stroke={MEDIAN_COLOR} strokeWidth={2.5} />
      {/* Stats labels */}
      <text x={cx + boxW / 2 + 8} y={toY(q3) + 4} fontSize={10} fill="#9ca3af">Q3: {shortLabel(q3)}</text>
      <text x={cx + boxW / 2 + 8} y={toY(median) + 4} fontSize={10} fill={MEDIAN_COLOR}>Med: {shortLabel(median)}</text>
      <text x={cx + boxW / 2 + 8} y={toY(q1) + 4} fontSize={10} fill="#9ca3af">Q1: {shortLabel(q1)}</text>
      {/* Outliers */}
      {outliers.map((v, i) => (
        <circle key={i} cx={cx} cy={toY(v)} r={3.5} fill={OUTLIER_COLOR} fillOpacity={0.8} />
      ))}
    </svg>
  );
}

function ViolinCellModal({ values, size, label }: { values: number[]; size: number; label: string }): ReactNode {
  const kdeData = useMemo(() => gaussianKde(values, 64), [values]);
  if (kdeData.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  return (
    <AreaChart width={size} height={size} data={kdeData} margin={{ ...ENLARGED_MARGIN, bottom: 48, left: 56 }}>
      <XAxis dataKey="value" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} label={{ value: label, position: "bottom", offset: 4, style: { fontSize: 12, fill: "#9ca3af" } }} />
      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} label={{ value: "Density", angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "#9ca3af" } }} />
      <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} />
      <Area type="monotone" dataKey="density" fill={VIOLIN_FILL} stroke={VIOLIN_STROKE} strokeWidth={2} />
    </AreaChart>
  );
}

function LineCellModal({
  data, size, xLabel, yLabel, colorByValues, groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  xLabel?: string;
  yLabel?: string;
  colorByValues?: string[];
  groupColorMap?: Map<string, string>;
}): ReactNode {
  const groupedLines = useMemo(() => {
    if (!colorByValues || !groupColorMap || groupColorMap.size <= 1) {
      return [{ key: "all", data: [...data].sort((a, b) => a.x - b.x), color: LINE_COLOR }];
    }
    const byGroup = new Map<string, { x: number; y: number; idx: number }[]>();
    for (const pt of data) {
      const group = colorByValues[pt.idx] ?? "";
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group)!.push(pt);
    }
    return [...byGroup.entries()].map(([key, pts]) => ({
      key,
      data: pts.sort((a, b) => a.x - b.x),
      color: groupColorMap.get(key) ?? LINE_COLOR,
    }));
  }, [data, colorByValues, groupColorMap]);

  if (data.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  // Compute global domain
  const domain = useMemo(() => {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    }
    return { x: [xMin, xMax] as [number, number], y: [yMin, yMax] as [number, number] };
  }, [data]);

  const m = xLabel ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const xSpan = domain.x[1] - domain.x[0] || 1;
  const ySpan = domain.y[1] - domain.y[0] || 1;

  const toX = (v: number) => m.left + ((v - domain.x[0]) / xSpan) * plotW;
  const toY = (v: number) => m.top + plotH - ((v - domain.y[0]) / ySpan) * plotH;

  // Tick values
  const xTicks = [domain.x[0], domain.x[0] + xSpan * 0.25, domain.x[0] + xSpan * 0.5, domain.x[0] + xSpan * 0.75, domain.x[1]];
  const yTicks = [domain.y[0], domain.y[0] + ySpan * 0.25, domain.y[0] + ySpan * 0.5, domain.y[0] + ySpan * 0.75, domain.y[1]];

  return (
    <svg width={size} height={size}>
      {/* Axes */}
      <line x1={m.left} y1={m.top + plotH} x2={m.left + plotW} y2={m.top + plotH} stroke="#3a3a5a" />
      <line x1={m.left} y1={m.top} x2={m.left} y2={m.top + plotH} stroke="#3a3a5a" />
      {xTicks.map((v, i) => (
        <text key={`xt-${i}`} x={toX(v)} y={m.top + plotH + 14} textAnchor="middle" fontSize={10} fill="#9ca3af">{shortLabel(v)}</text>
      ))}
      {yTicks.map((v, i) => (
        <text key={`yt-${i}`} x={m.left - 4} y={toY(v) + 3} textAnchor="end" fontSize={10} fill="#9ca3af">{shortLabel(v)}</text>
      ))}
      {xLabel && <text x={m.left + plotW / 2} y={size - 8} textAnchor="middle" fontSize={12} fill="#9ca3af">{xLabel}</text>}
      {yLabel && <text x={14} y={m.top + plotH / 2} textAnchor="middle" fontSize={12} fill="#9ca3af" transform={`rotate(-90, 14, ${m.top + plotH / 2})`}>{yLabel}</text>}
      {/* Lines per group */}
      {groupedLines.map((line) => {
        if (line.data.length < 2) return null;
        const pathD = line.data.map((pt, i) => `${i === 0 ? "M" : "L"}${toX(pt.x).toFixed(1)},${toY(pt.y).toFixed(1)}`).join(" ");
        return (
          <g key={line.key}>
            <path d={pathD} fill="none" stroke={line.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
            {line.data.map((pt, i) => (
              <circle key={i} cx={toX(pt.x)} cy={toY(pt.y)} r={2.5} fill={line.color} fillOpacity={0.8} />
            ))}
          </g>
        );
      })}
    </svg>
  );
}


const SELECTED_FILL = "#fbbf24";
const SELECTED_STROKE = "#f59e0b";
const SEL_DOT_SIZE = 12;

const ScatterCellGrid = memo(function ScatterCellGrid({
  data,
  size,
  colorByValues,
  colorByNumbers,
  onPointClick,
  onHoverPoint,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r?: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  onHoverPoint?: React.RefObject<HoverInfo | null>;
  groupColorMap?: Map<string, string>;
}) {
  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);

  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) {
      const v = colorByNumbers[pt.idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);

  const handleChartMouseMove = useCallback((state: Record<string, unknown>) => {
    if (!onHoverPoint) return;
    const payload = (state as { activePayload?: { payload?: { x: number; y: number; idx: number } }[] }).activePayload;
    if (payload && payload.length > 0 && payload[0]?.payload) {
      const cur = onHoverPoint.current;
      if (cur) cur.point = payload[0].payload;
    }
  }, [onHoverPoint]);

  const handleChartMouseLeave = useCallback(() => {
    if (onHoverPoint?.current) onHoverPoint.current.point = undefined;
  }, [onHoverPoint]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const pointColor = (idx: number): string => {
    if (colorByNumbers && numRange) {
      const v = colorByNumbers[idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        return gradientColor((v - numRange.min) / numRange.span);
      }
    }
    if (categoryColors && colorByValues) {
      return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    }
    return SCATTER_COLORS[0]!;
  };

  const pointRadius = Math.max(2, Math.min(4, 200 / data.length));

  return (
    <div style={{ width: size, height: size }}>
      <ScatterChart
        width={size}
        height={size}
        margin={CELL_MARGIN}
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleChartMouseLeave}
      >
        <XAxis dataKey="x" type="number" hide domain={["dataMin", "dataMax"]} />
        <YAxis dataKey="y" type="number" hide width={0} domain={["dataMin", "dataMax"]} />
        <Tooltip content={() => null} />
        <Scatter
          data={data}
          isAnimationActive={false}
          onClick={(point: Record<string, unknown>, _index: number, e: React.MouseEvent) => {
            if (!onPointClick) return;
            e.stopPropagation();
            const idx = (point as { idx?: number }).idx;
            if (typeof idx === "number") onPointClick(idx);
          }}
          cursor="pointer"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={pointColor(entry.idx)}
              fillOpacity={0.7}
              r={pointRadius}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Canvas-based scatter cell — replaces SVG for large datasets (N > 200)
// ---------------------------------------------------------------------------

const LTTB_GRID_TARGET = 350; // points per grid cell after downsampling

const CanvasScatterCell = memo(function CanvasScatterCell({
  data,
  size,
  row,
  col,
  fullResolution,
  colorByValues,
  colorByNumbers,
  onPointClick,
  onHoverPoint,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  row: number;
  col: number;
  fullResolution?: boolean;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  onHoverPoint?: React.RefObject<HoverInfo | null>;
  groupColorMap?: Map<string, string>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const displayData = useMemo(() => {
    if (fullResolution) return data;
    if (data.length <= 500) return data;
    return lttbDownsample(data, LTTB_GRID_TARGET);
  }, [data, fullResolution]);

  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);

  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) {
      const v = colorByNumbers[pt.idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);

  const pointColor = useCallback((idx: number): string => {
    if (colorByNumbers && numRange) {
      const v = colorByNumbers[idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        return gradientColor((v - numRange.min) / numRange.span);
      }
    }
    if (categoryColors && colorByValues) {
      return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    }
    return SCATTER_COLORS[0]!;
  }, [colorByNumbers, numRange, categoryColors, colorByValues]);

  // Domain for coordinate mapping
  const domain = useMemo(() => {
    if (data.length === 0) return null;
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const d of data) {
      if (d.x < xMin) xMin = d.x;
      if (d.x > xMax) xMax = d.x;
      if (d.y < yMin) yMin = d.y;
      if (d.y > yMax) yMax = d.y;
    }
    return {
      xMin, xMax, xSpan: xMax - xMin || 1,
      yMin, yMax, ySpan: yMax - yMin || 1,
    };
  }, [data]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !domain || displayData.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;
    const drawAsPixels = fullResolution && displayData.length > 1200;
    const r = drawAsPixels ? 1.25 : Math.max(2, Math.min(4, 200 / displayData.length));

    // Group points by color for batched drawing
    const colorGroups = new Map<string, { x: number; y: number }[]>();
    for (const pt of displayData) {
      const c = pointColor(pt.idx);
      if (!colorGroups.has(c)) colorGroups.set(c, []);
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      colorGroups.get(c)!.push({ x: px, y: py });
    }

    ctx.globalAlpha = 0.7;
    for (const [color, points] of colorGroups) {
      ctx.fillStyle = color;
      if (drawAsPixels) {
        for (const p of points) {
          ctx.fillRect(p.x - r / 2, p.y - r / 2, r, r);
        }
      } else {
        ctx.beginPath();
        for (const p of points) {
          ctx.moveTo(p.x + r, p.y);
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        }
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }, [displayData, fullResolution, size, domain, pointColor]);

  // Click handler — find nearest point within the rendered dot's radius.
  // Only consume the click if the cursor is essentially *on* a dot; otherwise
  // let it bubble so the parent cell's onClick opens the enlarged modal.
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onPointClick || !domain) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;

    // Hit radius must be tight (= rendered dot size + 1px tolerance),
    // otherwise every click in a dense cell lands "near" a point and
    // blocks the bubble-up that opens the enlarged modal view.
    const pointRadius = Math.max(2, Math.min(4, 200 / Math.max(displayData.length, 1)));
    const hitRadius = pointRadius + 1;
    let bestDist = Infinity;
    let bestIdx = -1;

    // Iterate displayed points. In full-resolution mode that is every point;
    // otherwise it is the sampled preview dots the user actually sees.
    for (const pt of displayData) {
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      const dx = mx - px;
      const dy = my - py;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = pt.idx;
      }
    }

    if (bestIdx >= 0 && bestDist <= hitRadius * hitRadius) {
      e.stopPropagation();
      onPointClick(bestIdx);
    }
  }, [onPointClick, displayData, domain, size]);

  // Mouse move handler for hover info
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onHoverPoint) return;
    if (!domain) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;

    let bestDist = Infinity;
    let bestPt: { x: number; y: number; idx: number } | undefined;
    const hitRadius = 12;

    for (const pt of displayData) {
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      const dx = mx - px;
      const dy = my - py;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestPt = pt;
      }
    }

    if (bestPt && bestDist <= hitRadius * hitRadius) {
      if (!onHoverPoint.current) onHoverPoint.current = { row, col };
      onHoverPoint.current.row = row;
      onHoverPoint.current.col = col;
      onHoverPoint.current.point = bestPt;
    } else if (onHoverPoint.current) {
      onHoverPoint.current.point = undefined;
    }
  }, [onHoverPoint, displayData, domain, size, row, col]);

  const handleMouseLeave = useCallback(() => {
    if (onHoverPoint?.current) onHoverPoint.current.point = undefined;
  }, [onHoverPoint]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ width: size, height: size, cursor: "pointer" }}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    />
  );
});

// ---------------------------------------------------------------------------
// Lazy cell wrapper — only renders chart when visible in viewport
// ---------------------------------------------------------------------------

const LAZY_THRESHOLD = 36; // minimum number of cells before enabling lazy rendering

function LazyCell({
  children,
  size,
  placeholder,
}: {
  children: ReactNode;
  size: number;
  placeholder?: ReactNode;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        }
      },
      { rootMargin: "100px" }, // preload cells 100px before they scroll into view
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: size, height: size }}>
      {isVisible ? children : (placeholder ?? <div style={{ width: size, height: size, background: "var(--gray-a2, rgba(99,102,241,0.02))" }} />)}
    </div>
  );
}

/**
 * Pure HTML overlay dot for the selected point. Renders on top of the
 * Recharts chart without causing the chart to re-render.
 */
function SelectionDot({
  data,
  size,
  selectedRowIdx,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  selectedRowIdx: number;
}): ReactNode {
  const pt = data.find(d => d.idx === selectedRowIdx);
  if (!pt || data.length < 2) return null;

  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const d of data) {
    if (d.x < xMin) xMin = d.x;
    if (d.x > xMax) xMax = d.x;
    if (d.y < yMin) yMin = d.y;
    if (d.y > yMax) yMax = d.y;
  }
  const xSpan = xMax - xMin || 1;
  const ySpan = yMax - yMin || 1;

  const chartW = size - CELL_MARGIN.left - CELL_MARGIN.right;
  const chartH = size - CELL_MARGIN.top - CELL_MARGIN.bottom;
  const px = CELL_MARGIN.left + ((pt.x - xMin) / xSpan) * chartW;
  const py = CELL_MARGIN.top + (1 - (pt.y - yMin) / ySpan) * chartH;
  const half = SEL_DOT_SIZE / 2;

  return (
    <div style={{
      position: "absolute",
      left: px - half, top: py - half,
      width: SEL_DOT_SIZE, height: SEL_DOT_SIZE,
      borderRadius: "50%",
      background: SELECTED_FILL,
      border: `2px solid ${SELECTED_STROKE}`,
      pointerEvents: "none",
      zIndex: 2,
      boxShadow: "0 0 6px rgba(251, 191, 36, 0.6)",
    }} />
  );
}

// ---------------------------------------------------------------------------
// Surface quality badge — compact overlay on 3D grid cells
// ---------------------------------------------------------------------------

/** Color for surface quality: high = vibrant, low = muted. */
function surfaceQualityColor(q: number): string {
  if (q > 0.6) return "#34d399";  // strong surface
  if (q > 0.4) return "#fbbf24";  // moderate
  if (q > 0.2) return "#60a5fa";  // weak
  return "#4b5563";               // minimal
}

const SurfaceQualityBadge = memo(function SurfaceQualityBadge({
  surfaceQuality,
  rPartial,
  r,
  cellSize,
}: {
  surfaceQuality: number;
  rPartial?: number;
  r: number;
  cellSize: number;
}) {
  const q = surfaceQuality;
  const color = surfaceQualityColor(q);
  const showPartial = rPartial != null && Number.isFinite(rPartial) && cellSize >= 70;

  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0,
      display: "flex", justifyContent: "space-between", alignItems: "flex-end",
      padding: "0 2px 1px",
      pointerEvents: "none",
    }}>
      {/* Surface quality indicator: small colored bar */}
      <div style={{
        height: 2, borderRadius: 1,
        width: `${Math.max(15, q * 100)}%`,
        background: color,
        opacity: 0.8,
      }} />
      {showPartial && (
        <div style={{
          fontSize: 7, fontWeight: 700, fontFamily: "var(--font-mono, monospace)",
          color: partialRColor(r, rPartial!),
          lineHeight: 1,
        }}>
          r<sub>p</sub>={rPartial!.toFixed(2)}
        </div>
      )}
    </div>
  );
});

function bestFitColor(bestFit: PairRelationshipScore["bestFit"]): string {
  if (bestFit === "linear") return "#34d399";
  if (bestFit === "quadratic" || bestFit === "cubic") return "#fbbf24";
  if (bestFit === "outliers") return "#f472b6";
  if (bestFit === "constant") return "#94a3b8";
  return "#6b7280";
}

function bestFitLabel(bestFit: PairRelationshipScore["bestFit"]): string {
  if (bestFit === "quadratic") return "quad";
  if (bestFit === "cubic") return "cubic";
  if (bestFit === "outliers") return "out";
  if (bestFit === "constant") return "flat";
  if (bestFit === "linear") return "lin";
  return "weak";
}

const RelationshipAtlasBadges = memo(function RelationshipAtlasBadges({
  relationship,
  cellSize,
}: {
  relationship?: PairRelationshipScore;
  cellSize: number;
}) {
  if (!relationship || cellSize < 58) return null;
  const color = bestFitColor(relationship.bestFit);
  const showCurve = !relationship.uninteresting && relationship.nonlinearLift > 0.08 && cellSize >= 72;
  const showOutlier = !relationship.uninteresting && relationship.outlierScore > 0.16 && cellSize >= 72;
  const showFlat = relationship.uninteresting && relationship.flatLineScore > 0.8 && cellSize >= 72;
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)" }}>
      <div style={{
        position: "absolute",
        top: 2,
        right: 2,
        padding: "1px 4px",
        borderRadius: 4,
        background: "rgba(10, 14, 22, 0.72)",
        color,
        fontSize: 8,
        fontWeight: 800,
        lineHeight: 1.2,
      }}>
        {bestFitLabel(relationship.bestFit)}
      </div>
      {showCurve && (
        <div style={{
          position: "absolute",
          left: 2,
          bottom: 2,
          padding: "1px 4px",
          borderRadius: 4,
          background: "rgba(10, 14, 22, 0.72)",
          color: "#fbbf24",
          fontSize: 8,
          fontWeight: 800,
          lineHeight: 1.2,
        }}>
          curve +{Math.round(relationship.nonlinearLift * 100)}
        </div>
      )}
      {showOutlier && (
        <div style={{
          position: "absolute",
          left: showCurve ? undefined : 2,
          right: showCurve ? 2 : undefined,
          bottom: 2,
          padding: "1px 4px",
          borderRadius: 4,
          background: "rgba(10, 14, 22, 0.72)",
          color: "#f472b6",
          fontSize: 8,
          fontWeight: 800,
          lineHeight: 1.2,
        }}>
          out {Math.round(relationship.outlierScore * 100)}
        </div>
      )}
      {showFlat && (
        <div style={{
          position: "absolute",
          left: 2,
          bottom: 2,
          padding: "1px 4px",
          borderRadius: 4,
          background: "rgba(10, 14, 22, 0.72)",
          color: "#94a3b8",
          fontSize: 8,
          fontWeight: 800,
          lineHeight: 1.2,
        }}>
          repeat {Math.round(relationship.flatLineScore * 100)}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Full-featured cells for the enlarged modal (with axes + tooltips)
// ---------------------------------------------------------------------------

function HistogramCellModal({ values, size, label, showAxisLabels }: { values: number[]; size: number; label: string; showAxisLabels?: boolean }): ReactNode {
  const bins = useMemo(() => binData(values), [values]);
  if (bins.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;
  return (
    <BarChart width={size} height={size} data={bins} margin={showAxisLabels ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN}>
      <XAxis dataKey="mid" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} label={showAxisLabels ? { value: label, position: "bottom", offset: 4, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} label={showAxisLabels ? { value: "Count", angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
      <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} formatter={(v) => [v ?? 0, "Count"]} labelFormatter={(l) => shortLabel(Number(l))} />
      <Bar dataKey="count" fill={HISTOGRAM_COLOR} stroke={HISTOGRAM_STROKE} strokeWidth={1} radius={[2, 2, 0, 0]} />
    </BarChart>
  );
}

/** Threshold above which the modal uses Plotly scattergl (WebGL) instead of Recharts SVG. */
const MODAL_WEBGL_THRESHOLD = 500;

function ScatterCellModal({ data, size, r: corr, colorByValues, colorByNumbers, onPointClick, onPointContextMenu, plotContext, selectedRowIdx, xLabel, yLabel, groupColorMap, fitMode = "none" }: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  plotContext?: CorrelogramPointContext;
  selectedRowIdx?: number;
  xLabel?: string;
  yLabel?: string;
  groupColorMap?: Map<string, string>;
  fitMode?: RelationshipFitMode;
}): ReactNode {
  const hoveredIdxRef = useRef<number | null>(null);
  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);
  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) { const v = colorByNumbers[pt.idx]; if (typeof v === "number" && Number.isFinite(v)) { if (v < min) min = v; if (v > max) max = v; } }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);
  const pointColor = useCallback((idx: number): string => {
    if (colorByNumbers && numRange) { const v = colorByNumbers[idx]; if (typeof v === "number" && Number.isFinite(v)) return gradientColor((v - numRange.min) / numRange.span); }
    if (categoryColors && colorByValues) return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    return SCATTER_COLORS[0]!;
  }, [colorByNumbers, numRange, categoryColors, colorByValues]);

  if (data.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  // For large N, or any fit overlay, use Plotly. The matrix remains cheap;
  // fits only materialize in this single enlarged plot.
  if (data.length > MODAL_WEBGL_THRESHOLD || fitMode !== "none") {
    return (
      <ScatterCellModalPlotly
        data={data}
        size={size}
        r={corr}
        pointColor={pointColor}
        onPointClick={onPointClick}
        onPointContextMenu={onPointContextMenu}
        plotContext={plotContext}
        selectedRowIdx={selectedRowIdx}
        xLabel={xLabel}
        yLabel={yLabel}
        fitMode={fitMode}
      />
    );
  }

  // Small dataset — keep Recharts SVG for rich interactivity
  const pr = Math.max(3, Math.min(6, 400 / data.length));
  const selPr = Math.max(pr * 2, 8);

  const sortedData = [...data].sort((a, b) =>
    (a.idx === selectedRowIdx ? 1 : 0) - (b.idx === selectedRowIdx ? 1 : 0),
  );

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onPointContextMenu || hoveredIdxRef.current == null) return;
    event.preventDefault();
    event.stopPropagation();
    onPointContextMenu(hoveredIdxRef.current, event, plotContext);
  };

  return (
    <div style={{ width: size, height: size, position: "relative" }} onContextMenu={handleContextMenu}>
      {Number.isFinite(corr) && (
        <div style={{ position: "absolute", top: 8, right: 16, fontSize: 13, fontWeight: 700, color: rColor(corr), zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)" }}>
          r = {corr.toFixed(3)}
        </div>
      )}
      <ScatterChart
        width={size}
        height={size}
        margin={xLabel ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN}
        onMouseMove={(state: Record<string, unknown>) => {
          const payload = (state as { activePayload?: { payload?: { idx?: number } }[] }).activePayload;
          const idx = payload?.[0]?.payload?.idx;
          hoveredIdxRef.current = typeof idx === "number" ? idx : null;
        }}
        onMouseLeave={() => {
          hoveredIdxRef.current = null;
        }}
      >
        <XAxis dataKey="x" type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} domain={["dataMin", "dataMax"]} label={xLabel ? { value: xLabel, position: "bottom", offset: 4, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
        <YAxis dataKey="y" type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} domain={["dataMin", "dataMax"]} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
        <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} formatter={(v) => shortLabel(Number(v ?? 0))} />
        <Scatter
          data={sortedData}
          isAnimationActive={false}
          onClick={(point: Record<string, unknown>) => {
            const idx = (point as { idx?: number }).idx;
            if (typeof idx === "number" && onPointClick) onPointClick(idx);
          }}
          cursor="pointer"
        >
          {sortedData.map((entry, index) => {
            const sel = entry.idx === selectedRowIdx;
            return <Cell key={`c-${index}`} fill={sel ? SELECTED_FILL : pointColor(entry.idx)} stroke={sel ? SELECTED_STROKE : "none"} strokeWidth={sel ? 2 : 0} fillOpacity={sel ? 1 : 0.7} r={sel ? selPr : pr} />;
          })}
        </Scatter>
      </ScatterChart>
    </div>
  );
}

/**
 * Plotly-based modal scatter using scattergl (WebGL) for large datasets.
 * Shows ALL points (no downsampling) since it's a single chart with WebGL.
 */
function ScatterCellModalPlotly({ data, size, r: corr, pointColor, onPointClick, onPointContextMenu, plotContext, selectedRowIdx, xLabel, yLabel, fitMode = "none" }: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r: number;
  pointColor: (idx: number) => string;
  onPointClick?: (rowIdx: number) => void;
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  plotContext?: CorrelogramPointContext;
  selectedRowIdx?: number;
  xLabel?: string;
  yLabel?: string;
  fitMode?: RelationshipFitMode;
}): ReactNode {
  const { traces, layout, fit } = useMemo(() => {
    const colors = data.map(pt => pointColor(pt.idx));
    const markerSize = Math.max(2.5, Math.min(5, 400 / data.length));
    const fit = fitTraceForMode(data, fitMode);

    const mainTrace: Partial<Plotly.PlotData> = {
      x: data.map(d => d.x),
      y: data.map(d => d.y),
      customdata: data.map(d => d.idx) as unknown as Plotly.Datum[],
      type: "scattergl" as Plotly.PlotType,
      mode: "markers",
      marker: {
        size: markerSize,
        color: colors,
        opacity: 0.7,
      },
      hovertemplate: `${xLabel ?? "x"}: %{x}<br>${yLabel ?? "y"}: %{y}<extra></extra>`,
    };

    const allTraces: Partial<Plotly.PlotData>[] = [mainTrace];
    if (fit) allTraces.push(fit.trace);

    // Highlighted selected point
    if (selectedRowIdx != null) {
      const sel = data.find(d => d.idx === selectedRowIdx);
      if (sel) {
        allTraces.push({
          x: [sel.x],
          y: [sel.y],
          type: "scattergl" as Plotly.PlotType,
          mode: "markers",
          marker: {
            size: 12,
            color: SELECTED_FILL,
            line: { color: SELECTED_STROKE, width: 2 },
          },
          showlegend: false,
          hoverinfo: "skip",
        } as Partial<Plotly.PlotData>);
      }
    }

    const plotLayout: Partial<Plotly.Layout> = {
      width: size,
      height: size,
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      margin: { t: 30, r: 16, b: xLabel ? 52 : 32, l: yLabel ? 56 : 48 },
      xaxis: {
        title: xLabel ? { text: xLabel, font: { size: 12, color: "#9ca3af" } } : undefined,
        gridcolor: "#2a2a4a",
        zerolinecolor: "#3a3a5a",
        tickfont: { size: 10, color: "#9ca3af" },
      },
      yaxis: {
        title: yLabel ? { text: yLabel, font: { size: 12, color: "#9ca3af" } } : undefined,
        gridcolor: "#2a2a4a",
        zerolinecolor: "#3a3a5a",
        tickfont: { size: 10, color: "#9ca3af" },
      },
      showlegend: false,
      font: { color: "#9ca3af" },
    };

    return { traces: allTraces, layout: plotLayout, fit };
  }, [data, pointColor, selectedRowIdx, xLabel, yLabel, size, fitMode]);

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      {Number.isFinite(corr) && (
        <div style={{ position: "absolute", top: 8, right: 16, fontSize: 13, fontWeight: 700, color: rColor(corr), zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)" }}>
          r = {corr.toFixed(3)}
        </div>
      )}
      {fit && (
        <div style={{
          position: "absolute",
          top: 28,
          right: 16,
          fontSize: 11,
          fontWeight: 700,
          color: fitMode === "rbf" ? "#2dd4bf" : "#fbbf24",
          zIndex: 1,
          pointerEvents: "none",
          fontFamily: "var(--font-mono, monospace)",
        }}>
          {fit.label}{fit.scoreLabel ? ` · ${fit.scoreLabel}` : ""}
        </div>
      )}
      <PlotWithLoader
        traces={traces as Plotly.Data[]}
        layout={layout as Plotly.Layout}
        size={size}
        onPointClick={onPointClick}
        onPointContextMenu={onPointContextMenu}
        plotContext={plotContext}
      />
    </div>
  );
}

/** Plotly wrapper that shows an indeterminate progress bar while the
 *  scattergl context finishes rendering — meaningful for cells with many
 *  thousands of points where the first paint isn't instant. */
function PlotWithLoader({
  traces, layout, size, onPointClick, onPointContextMenu, plotContext,
}: {
  traces: Plotly.Data[];
  layout: Plotly.Layout;
  size: number;
  onPointClick?: (rowIdx: number) => void;
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  plotContext?: CorrelogramPointContext;
}): ReactNode {
  const [ready, setReady] = useState(false);
  const hoveredIdxRef = useRef<number | null>(null);
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onPointContextMenu || hoveredIdxRef.current == null) return;
    event.preventDefault();
    event.stopPropagation();
    onPointContextMenu(hoveredIdxRef.current, event, plotContext);
  };
  return (
    <div style={{ position: "relative", width: size, height: size }} onContextMenu={handleContextMenu}>
      {!ready && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "var(--color-panel, rgba(255,255,255,0.6))",
          pointerEvents: "none",
        }}>
          <div style={{ width: "60%", height: 3, borderRadius: 2, background: "var(--gray-a4)", overflow: "hidden" }}>
            <div style={{
              width: "30%", height: "100%",
              background: "var(--accent-9, #3b82f6)",
              animation: "correlogram-loader 1.1s linear infinite",
            }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--gray-10)" }}>Rendering plot…</div>
          <style>{`@keyframes correlogram-loader { 0% { transform: translateX(-100%); } 100% { transform: translateX(333%); } }`}</style>
        </div>
      )}
      <Plot
        data={traces}
        layout={layout}
        config={{ displayModeBar: true, modeBarButtonsToRemove: ["toImage" as Plotly.ModeBarDefaultButtons], responsive: true }}
        style={{ width: size, height: size }}
        onInitialized={() => setReady(true)}
        onUpdate={() => setReady(true)}
        onClick={(event) => {
          if (!onPointClick || !event.points?.[0]) return;
          const pt = event.points[0];
          const idx = (pt as { customdata?: number }).customdata;
          if (typeof idx === "number") onPointClick(idx);
        }}
        onHover={(event) => {
          const pt = event.points?.[0];
          const idx = (pt as { customdata?: number } | undefined)?.customdata;
          hoveredIdxRef.current = typeof idx === "number" ? idx : null;
        }}
        onUnhover={() => {
          hoveredIdxRef.current = null;
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tick label cell (plain HTML)
// ---------------------------------------------------------------------------

const TICK_STYLE: React.CSSProperties = {
  fontSize: 8, color: "var(--gray-9, #6b7280)", fontFamily: "var(--font-mono, monospace)",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

function BottomTickCell({ range, size }: { range: ColRange | null; size: number }): ReactNode {
  if (!range) return <div style={{ width: size }} />;
  return (
    <div style={{ width: size, height: TICK_ROW_H, display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "2px 2px 0" }}>
      <span style={TICK_STYLE}>{shortLabel(range.min)}</span>
      <span style={TICK_STYLE}>{shortLabel(range.max)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell context menu
// ---------------------------------------------------------------------------

type CellMenuState = {
  x: number;
  y: number;
  row: number;
  col: number;
};

function CorrelogramCellContextMenu({
  menu,
  rowCol,
  colCol,
  is3D,
  canOpenPlotBuilder,
  onOpenPlotBuilder,
  onOpenInteractive,
  onClose,
}: {
  menu: CellMenuState;
  rowCol: CorrelogramColumn;
  colCol: CorrelogramColumn;
  is3D: boolean;
  canOpenPlotBuilder: boolean;
  onOpenPlotBuilder: () => void;
  onOpenInteractive: () => void;
  onClose: () => void;
}): ReactNode {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const handleScroll = () => onClose();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  const menuW = 236;
  const menuH = 154;
  const x = menu.x + menuW > window.innerWidth ? window.innerWidth - menuW - 8 : menu.x;
  const y = menu.y + menuH > window.innerHeight ? window.innerHeight - menuH - 8 : menu.y;

  const buttonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    border: "none",
    borderRadius: 4,
    background: "transparent",
    color: "var(--gray-12, #f0f0f5)",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    padding: "7px 10px",
    textAlign: "left",
  };

  const hoverIn = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.style.background = "var(--accent-a3, rgba(99,102,241,0.16))";
  };
  const hoverOut = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.style.background = "transparent";
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: x,
        top: y,
        zIndex: 99999,
        width: menuW,
        border: "1px solid var(--gray-a5, rgba(255,255,255,0.16))",
        borderRadius: 8,
        background: "var(--color-surface, #151922)",
        color: "var(--gray-12, #f0f0f5)",
        boxShadow: "0 12px 36px rgba(0,0,0,0.45)",
        padding: "5px 0",
        fontFamily: "var(--fc-font-family, inherit)",
      }}
    >
      <div style={{
        padding: "4px 10px 7px",
        borderBottom: "1px solid var(--gray-a4, rgba(255,255,255,0.12))",
      }}>
        <div style={{ fontSize: 10, color: "var(--gray-9, #8b949e)", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.04em" }}>
          Correlogram cell
        </div>
        <div style={{ marginTop: 2, fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {colCol.header} vs {rowCol.header}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenPlotBuilder}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={buttonStyle}
      >
        {canOpenPlotBuilder ? "Show in Plot Builder" : "Open Distribution"}
      </button>
      <button
        type="button"
        onClick={onOpenInteractive}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={buttonStyle}
      >
        Open Interactive Plot
      </button>
      <div style={{ padding: "5px 10px 4px", borderTop: "1px solid var(--gray-a4, rgba(255,255,255,0.12))", fontSize: 10, lineHeight: 1.4, color: "var(--gray-9, #8b949e)" }}>
        {is3D
          ? "Builder opens the same X/Y/Z axes."
          : canOpenPlotBuilder
          ? "Right-click a point for lineage, mesh, solver, and flowfield actions."
          : "Distributions do not have an X/Y pair, so they open as enlarged inspection views."}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Expanded cell overlay (inline, fills 90% of grid area)
// ---------------------------------------------------------------------------

function ExpandedCellOverlay({
  row,
  col,
  numericColumns,
  matrixData,
  containerSize,
  colorByValues,
  colorByNumbers,
  groupColorMap,
  zAxisKey,
  onClose,
  onPointClick,
  onPointContextMenu,
  selectedRowIdx,
  offDiagPlotType = "scatter",
  diagPlotType = "histogram",
  fitMode = "none",
}: {
  row: number;
  col: number;
  numericColumns: CorrelogramColumn[];
  matrixData: Record<string, { pairs?: { x: number; y: number; idx: number }[]; triples?: { x: number; y: number; z: number; idx: number }[]; values?: number[]; r?: number; rPartial?: number; multiR?: number; surfaceQuality?: number; relationship?: PairRelationshipScore }>;
  containerSize: { width: number; height: number };
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
  zAxisKey?: string;
  onClose: () => void;
  onPointClick?: (rowIdx: number) => void;
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent, context?: CorrelogramPointContext) => void;
  selectedRowIdx?: number;
  offDiagPlotType?: OffDiagPlotType;
  diagPlotType?: DiagPlotType;
  fitMode?: RelationshipFitMode;
}): ReactNode {
  const cellData = matrixData[`${row}-${col}`];
  const rowCol = numericColumns[row]!;
  const colCol = numericColumns[col]!;
  const isDiagonal = row === col;
  const is3D = offDiagPlotType === "scatter3d" && !isDiagonal && !!cellData?.triples;
  const title = isDiagonal
    ? rowCol.header
    : is3D
    ? `${colCol.header} vs ${rowCol.header} vs ${zAxisKey ?? "Z"}`
    : `${colCol.header} vs ${rowCol.header}`;

  const chartSize = Math.max(
    170,
    Math.floor(Math.min(containerSize.width - 140, containerSize.height - 190)),
  );

  const { openPlotBuilder } = useProvenanceContext();
  const plotContext = useMemo<CorrelogramPointContext | undefined>(() => {
    if (isDiagonal) return undefined;
    return {
      xField: colCol.key,
      yField: rowCol.key,
      zField: is3D ? zAxisKey : undefined,
      xLabel: colCol.header,
      yLabel: rowCol.header,
      zLabel: is3D ? zAxisKey : undefined,
    };
  }, [colCol.header, colCol.key, is3D, isDiagonal, rowCol.header, rowCol.key, zAxisKey]);

  const handleOpenInBuilder = () => {
    if (!isDiagonal) {
      openPlotBuilder(colCol.key, rowCol.key, is3D ? zAxisKey : undefined);
    }
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute", inset: 0, zIndex: 20,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--color-surface, #1a1a2e)",
          border: "1px solid var(--gray-a5, #2a2a4a)",
          borderRadius: 12, padding: 16,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          boxShadow: "0 12px 40px rgba(0,0,0,.5)",
          maxWidth: "92%", maxHeight: "92%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gray-12, #f0f0f5)", whiteSpace: "nowrap" }}>{title}</span>
            {!isDiagonal && Number.isFinite(cellData?.r) && (
              <span style={{ fontSize: 12, fontWeight: 700, color: rColor(cellData!.r!), fontFamily: "var(--font-mono, monospace)" }}>
                r = {cellData!.r!.toFixed(3)}
              </span>
            )}
            {!isDiagonal && cellData?.relationship && (
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: bestFitColor(cellData.relationship.bestFit),
                padding: "1px 6px",
                borderRadius: 4,
                background: "var(--gray-a3)",
              }}>
                {bestFitLabel(cellData.relationship.bestFit)}
                {cellData.relationship.nonlinearLift > 0.08 ? ` · curve +${Math.round(cellData.relationship.nonlinearLift * 100)}` : ""}
              </span>
            )}
            {is3D && cellData?.surfaceQuality != null && (
              <span style={{
                fontSize: 11, fontWeight: 600, fontFamily: "var(--font-mono, monospace)",
                color: surfaceQualityColor(cellData.surfaceQuality),
                padding: "1px 6px", borderRadius: 4,
                background: "var(--gray-a3)",
              }}>
                surface: {(cellData.surfaceQuality * 100).toFixed(0)}%
              </span>
            )}
            {!isDiagonal && fitMode !== "none" && (
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                color: fitMode === "rbf" ? "#2dd4bf" : "#fbbf24",
                padding: "1px 6px",
                borderRadius: 4,
                background: "var(--gray-a3)",
              }}>
                fit: {FIT_MODES.find((mode) => mode.key === fitMode)?.short ?? fitMode}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "var(--gray-9)", fontSize: 18, cursor: "pointer", padding: "2px 6px", lineHeight: 1, flexShrink: 0 }}
          >
            &times;
          </button>
        </div>
        {!isDiagonal && (
          <div style={{
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            color: "var(--gray-9, #8b949e)",
            fontSize: 11,
            lineHeight: 1.4,
          }}>
            <span>Click a point to select its row and light up lineage.</span>
            <span>Right-click a point for the same provenance menu as the Plot Builder.</span>
          </div>
        )}
        <div style={{ position: "relative", background: "var(--gray-2, #1c1c22)", borderRadius: 8, padding: 4 }}>
          {isDiagonal
            ? (diagPlotType === "boxplot"
              ? <BoxPlotCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} />
              : diagPlotType === "violin"
              ? <ViolinCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} />
              : <HistogramCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} showAxisLabels />)
            : is3D
            ? <Scatter3DCellModal
                data={cellData?.triples ?? []}
                size={chartSize}
                r={cellData?.r ?? NaN}
                rPartial={cellData?.rPartial}
                multiR={cellData?.multiR}
                xLabel={colCol.header}
                yLabel={rowCol.header}
                zLabel={zAxisKey}
                colorByValues={colorByValues}
                colorByNumbers={colorByNumbers}
                groupColorMap={groupColorMap}
                onPointClick={onPointClick}
                onPointContextMenu={onPointContextMenu}
                plotContext={plotContext}
                selectedRowIdx={selectedRowIdx}
                fitMode={fitMode}
              />
            : (offDiagPlotType === "line"
              ? <LineCellModal data={cellData?.pairs ?? []} size={chartSize} xLabel={colCol.header} yLabel={rowCol.header} colorByValues={colorByValues} groupColorMap={groupColorMap} />
              // Click-to-enlarge should reveal the underlying data. Hexbin
              // is a thumbnail density affordance, not something a user
              // should have to undo manually when drilling in.
              : <ScatterCellModal data={cellData?.pairs ?? []} size={chartSize} r={cellData?.r ?? NaN} colorByValues={colorByValues} colorByNumbers={colorByNumbers} onPointClick={onPointClick} onPointContextMenu={onPointContextMenu} plotContext={plotContext} selectedRowIdx={selectedRowIdx} xLabel={colCol.header} yLabel={rowCol.header} groupColorMap={groupColorMap} fitMode={fitMode} />)
          }
          {!isDiagonal && (
            <button
              onClick={handleOpenInBuilder}
              style={{
                position: "absolute", bottom: 16, right: 16,
                padding: "5px 14px", borderRadius: 6,
                border: "1px solid var(--accent-7, #4f46e5)",
                background: "var(--color-surface, rgba(26,26,46,0.9))",
                color: "var(--accent-11, #a5b4fc)",
                fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                opacity: 0.7, transition: "opacity 150ms",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "0.7"; }}
            >
              {is3D ? "Open 3D in Plot Builder" : "Open in Plot Builder"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hover info panel (right sidebar)
// ---------------------------------------------------------------------------

interface HoverInfo {
  row: number;
  col: number;
  point?: { x: number; y: number; idx: number };
}

const INFO_PANEL_W = 200;

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 10, color: "var(--gray-9, #6b7280)", textTransform: "uppercase",
  letterSpacing: "0.05em", marginBottom: 2,
};
const VALUE_STYLE: React.CSSProperties = {
  fontSize: 13, color: "var(--gray-12, #f0f0f5)", fontWeight: 600,
  fontFamily: "var(--font-mono, monospace)", wordBreak: "break-all",
};

// ---------------------------------------------------------------------------
// Contextual guide — replaces idle text in hover panel
// ---------------------------------------------------------------------------

const GUIDE_HEAD: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: "var(--gray-11, #c0c0c8)",
  textTransform: "uppercase", letterSpacing: "0.04em",
  marginTop: 6, marginBottom: 3,
};

const GUIDE_TEXT: React.CSSProperties = {
  fontSize: 10, lineHeight: 1.5, color: "var(--gray-9, #6b7280)",
};

const GUIDE_STAT: React.CSSProperties = {
  fontFamily: "var(--font-mono, monospace)", fontWeight: 700,
};

const GUIDE_EXAMPLE: React.CSSProperties = {
  fontSize: 9, lineHeight: 1.4, color: "var(--gray-8, #4b5563)",
  borderLeft: "2px solid var(--gray-a4)", paddingLeft: 6,
  marginTop: 3, marginBottom: 2,
};

function CorrelogramGuide({ zAxisKey }: { zAxisKey?: string }): ReactNode {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ paddingTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ ...GUIDE_TEXT, marginBottom: 4 }}>
        Hover over the matrix to see cell and point details. Click a cell for an interactive plot; right-click a cell to send it to the Plot Builder.
      </div>

      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          background: "none", border: "1px solid var(--gray-a4)",
          borderRadius: 4, padding: "3px 6px", cursor: "pointer",
          fontSize: 10, color: "var(--accent-11, #a5b4fc)",
          textAlign: "left",
        }}
      >
        {expanded ? "▾ Hide guide" : "▸ How to read this"}
      </button>

      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
          {/* --- Basics --- */}
          <div style={GUIDE_HEAD}>Basics</div>
          <div style={GUIDE_TEXT}>
            Each cell shows the relationship between two variables.
            <strong> Diagonal</strong> = distribution of one variable.
            <strong> Off-diagonal</strong> = how two variables relate.
          </div>

          <div style={GUIDE_HEAD}>Pearson r</div>
          <div style={GUIDE_TEXT}>
            <span style={{ ...GUIDE_STAT, color: "#34d399" }}>r</span> measures
            linear correlation from −1 to +1.
          </div>
          <div style={GUIDE_EXAMPLE}>
            r = 0.95 → strong positive trend<br />
            r = −0.3 → weak inverse trend<br />
            r ≈ 0 → no linear link
          </div>

          {/* --- 3D-specific guide --- */}
          {zAxisKey && (
            <>
              <div style={{ borderTop: "1px solid var(--gray-a4)", marginTop: 6, paddingTop: 6 }} />
              <div style={{ ...GUIDE_HEAD, color: "var(--blue-11)" }}>
                3D mode (Z = {zAxisKey})
              </div>

              <div style={GUIDE_HEAD}>Partial correlation r<sub>p</sub></div>
              <div style={GUIDE_TEXT}>
                <span style={{ ...GUIDE_STAT, color: "#60a5fa" }}>r<sub>p</sub></span> is
                the correlation between X and Y <em>after removing Z&apos;s influence</em>.
                Compare it with r:
              </div>
              <div style={GUIDE_EXAMPLE}>
                r = 0.8, r<sub>p</sub> = 0.2 → Z explains most of the
                X-Y link. Without Z, X and Y are nearly independent.
              </div>
              <div style={GUIDE_EXAMPLE}>
                r = 0.1, r<sub>p</sub> = 0.7 → X and Y look uncorrelated
                in 2D, but are strongly linked once you account for Z.
                This is <strong>Simpson&apos;s paradox</strong>.
              </div>
              <div style={GUIDE_EXAMPLE}>
                r ≈ r<sub>p</sub> → Z has no effect on the X-Y
                relationship. The coupling is independent of Z.
              </div>

              <div style={GUIDE_HEAD}>Simpson&apos;s paradox</div>
              <div style={GUIDE_TEXT}>
                A trend that appears in 2D can reverse or vanish when a
                third variable is accounted for — or vice versa. The
                partial correlation catches this automatically.
              </div>
              <div style={GUIDE_EXAMPLE}>
                Example: drag increases with speed (r &gt; 0), but after
                controlling for angle-of-attack (Z), drag may <em>decrease</em>{" "}
                at each fixed angle — the overall trend was driven by
                angle, not speed alone.
              </div>

              <div style={GUIDE_HEAD}>Multiple R</div>
              <div style={GUIDE_TEXT}>
                <span style={{ ...GUIDE_STAT, color: "#a78bfa" }}>R</span> measures
                how well X and Z <em>together</em> predict Y. High R means the
                three axes form a clean surface (plane, bowl, saddle).
              </div>

              <div style={GUIDE_HEAD}>Surface quality</div>
              <div style={GUIDE_TEXT}>
                A composite score combining R and the partial-r shift.
                Cells with high surface quality are the most
                &ldquo;interesting&rdquo; 3D relationships.
              </div>
              <div style={GUIDE_EXAMPLE}>
                <span style={{ color: "#34d399" }}>■</span> &gt;60% → Strong 3D coupling<br />
                <span style={{ color: "#fbbf24" }}>■</span> 30–60% → Moderate structure<br />
                <span style={{ color: "#60a5fa" }}>■</span> &lt;30% → Weak 3D relationship
              </div>

              <div style={GUIDE_HEAD}>Which axes are coupled?</div>
              <div style={GUIDE_TEXT}>
                Look for cells where:
              </div>
              <div style={GUIDE_EXAMPLE}>
                1. r<sub>p</sub> ≠ r → Z mediates or confounds<br />
                2. Surface quality is high → clean 3D shape<br />
                3. R is high but r is low → hidden 3D structure<br />
                Click any cell to explore the 3D scatter interactively.
              </div>
            </>
          )}

          {!zAxisKey && (
            <>
              <div style={{ borderTop: "1px solid var(--gray-a4)", marginTop: 6, paddingTop: 4 }} />
              <div style={GUIDE_TEXT}>
                <strong>Tip:</strong> Select a <span style={{ color: "var(--blue-11)" }}>Z axis</span>{" "}
                above to enable 3D mode. Each cell becomes a 3D scatter
                plot, and you&apos;ll see partial correlations that reveal
                hidden coupling between axes.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HoverInfoPanel({
  hoverRef,
  numericColumns,
  matrixData,
  rows,
  zAxisKey,
}: {
  hoverRef: React.RefObject<HoverInfo | null>;
  numericColumns: CorrelogramColumn[];
  matrixData: Record<string, { pairs?: { x: number; y: number; idx: number }[]; triples?: { x: number; y: number; z: number; idx: number }[]; values?: number[]; r?: number; rPartial?: number; multiR?: number; surfaceQuality?: number; relationship?: PairRelationshipScore }>;
  rows: Record<string, unknown>[];
  zAxisKey?: string;
}) {
  const [, tick] = useState(0);
  const prev = useRef<string>("");

  useEffect(() => {
    let raf: number;
    const poll = () => {
      const key = hoverRef.current
        ? `${hoverRef.current.row}-${hoverRef.current.col}-${hoverRef.current.point?.idx ?? ""}`
        : "";
      if (key !== prev.current) {
        prev.current = key;
        tick(c => c + 1);
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [hoverRef]);

  const hover = hoverRef.current;
  const rowCol = hover ? numericColumns[hover.row] : null;
  const colCol = hover ? numericColumns[hover.col] : null;
  const isDiag = hover ? hover.row === hover.col : false;
  const cellData = hover ? matrixData[`${hover.row}-${hover.col}`] : null;
  const hoveredRow = hover?.point != null ? rows[hover.point.idx] : null;
  const runName = hoveredRow?.name as string | undefined;

  return (
    <div style={{
      width: INFO_PANEL_W, flexShrink: 0,
      borderLeft: "1px solid var(--gray-a4, #2a2a4a)",
      padding: "12px 10px",
      display: "flex", flexDirection: "column", gap: 12,
      fontSize: 12, color: "var(--gray-11, #9ca3af)",
      overflow: "hidden",
    }}>
      {!hover && (
        <CorrelogramGuide zAxisKey={zAxisKey} />
      )}

      {hover && isDiag && rowCol && (
        <>
          <div>
            <div style={LABEL_STYLE}>Histogram</div>
            <div style={VALUE_STYLE}>{rowCol.header}</div>
          </div>
          <div>
            <div style={LABEL_STYLE}>Points</div>
            <div style={VALUE_STYLE}>{cellData?.values?.length ?? 0}</div>
          </div>
        </>
      )}

      {hover && !isDiag && rowCol && colCol && (
        <>
          <div>
            <div style={LABEL_STYLE}>Plot</div>
            <div style={{ ...VALUE_STYLE, fontSize: 12 }}>
              {colCol.header} <span style={{ color: "var(--gray-8)", fontWeight: 400 }}>vs</span> {rowCol.header}
            </div>
          </div>
          {cellData?.r != null && Number.isFinite(cellData.r) && (
            <div>
              <div style={LABEL_STYLE}>Correlation (r)</div>
              <div style={{ ...VALUE_STYLE, color: rColor(cellData.r!) }}>
                {cellData.r!.toFixed(4)}
              </div>
            </div>
          )}
          {cellData?.relationship && (
            <div>
              <div style={LABEL_STYLE}>Relationship</div>
              <div style={{ ...VALUE_STYLE, color: bestFitColor(cellData.relationship.bestFit) }}>
                {bestFitLabel(cellData.relationship.bestFit)}
              </div>
              <div style={{ fontSize: 9, color: "var(--gray-8)", marginTop: 2, lineHeight: 1.35 }}>
                r2 {cellData.relationship.linearR2.toFixed(2)}
                {" · "}curve +{Math.round(cellData.relationship.nonlinearLift * 100)}
                {" · "}out {Math.round(cellData.relationship.outlierScore * 100)}
              </div>
            </div>
          )}
          {cellData?.rPartial != null && Number.isFinite(cellData.rPartial) && (
            <div>
              <div style={LABEL_STYLE}>Partial r (ctrl Z)</div>
              <div style={{ ...VALUE_STYLE, color: partialRColor(cellData.r ?? 0, cellData.rPartial!) }}>
                {cellData.rPartial!.toFixed(4)}
              </div>
              <div style={{ fontSize: 9, color: "var(--gray-8)", marginTop: 1 }}>
                {Math.abs((cellData.r ?? 0) - Math.abs(cellData.rPartial!)) > 0.2
                  ? "Z explains much of this coupling"
                  : "Axes coupled independently of Z"}
              </div>
            </div>
          )}
          {cellData?.multiR != null && Number.isFinite(cellData.multiR) && (
            <div>
              <div style={LABEL_STYLE}>Multiple R</div>
              <div style={{ ...VALUE_STYLE, color: "#a78bfa" }}>
                {cellData.multiR!.toFixed(4)}
              </div>
            </div>
          )}
          {cellData?.surfaceQuality != null && (
            <div>
              <div style={LABEL_STYLE}>Surface quality</div>
              <div style={{
                ...VALUE_STYLE,
                color: surfaceQualityColor(cellData.surfaceQuality),
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {(cellData.surfaceQuality * 100).toFixed(0)}%
                <div style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: "var(--gray-a4)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${cellData.surfaceQuality * 100}%`,
                    height: "100%",
                    borderRadius: 2,
                    background: surfaceQualityColor(cellData.surfaceQuality),
                  }} />
                </div>
              </div>
              <div style={{ fontSize: 9, color: "var(--gray-8)", marginTop: 1 }}>
                {cellData.surfaceQuality > 0.6
                  ? "Strong 3D coupling"
                  : cellData.surfaceQuality > 0.3
                  ? "Moderate 3D structure"
                  : "Weak 3D relationship"}
              </div>
            </div>
          )}
          <div>
            <div style={LABEL_STYLE}>Points</div>
            <div style={VALUE_STYLE}>{cellData?.triples?.length ?? cellData?.pairs?.length ?? 0}</div>
          </div>
          <div style={{ borderTop: "1px solid var(--gray-a4, #2a2a4a)", paddingTop: 10 }}>
            <div style={LABEL_STYLE}>Actions</div>
            <div style={{ fontSize: 10, lineHeight: 1.5, color: "var(--gray-9, #6b7280)" }}>
              Click the cell to inspect all points with axes and tooltips.
              Right-click the cell for Plot Builder.
              Right-click a point for lineage, solver, mesh, and flowfield actions.
            </div>
          </div>
        </>
      )}

      {hover?.point != null && rowCol && colCol && !isDiag && (
        <>
          <div style={{ borderTop: "1px solid var(--gray-a4, #2a2a4a)", paddingTop: 10 }}>
            <div style={LABEL_STYLE}>{colCol.header}</div>
            <div style={VALUE_STYLE}>{shortLabel(hover.point.x)}</div>
          </div>
          <div>
            <div style={LABEL_STYLE}>{rowCol.header}</div>
            <div style={VALUE_STYLE}>{shortLabel(hover.point.y)}</div>
          </div>
          {runName && (
            <div>
              <div style={LABEL_STYLE}>Run</div>
              <div style={{ ...VALUE_STYLE, fontSize: 11, fontWeight: 500 }}>{runName}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function Correlogram({
  rows, numericColumns, groups, colorByKey, zAxisKey, zAxisLabel, zValueResolver,
  onPointClick, onPointContextMenu, selectedRowIdx,
  offDiagPlotType: offDiagProp, diagPlotType: diagProp,
  onOffDiagPlotTypeChange, onDiagPlotTypeChange,
  maxColumns = MAX_AUTO_COLUMNS,
  defaultPruneBoringAxes = false,
  defaultHideWeakCorrelations = false,
  weakCorrelationThreshold = 0.18,
}: CorrelogramProps): ReactNode {
  const [localOffDiag, setLocalOffDiag] = useState<OffDiagPlotType>("scatter");
  const [localDiag, setLocalDiag] = useState<DiagPlotType>("histogram");
  const [correlationView, setCorrelationView] = useState<CorrelationViewMode>(() => zAxisKey ? "3d" : "2d");
  const [selectionMode, setSelectionMode] = useState<CorrelogramSelectionMode>(() => zAxisKey ? "interesting" : "random");
  const [randomSeed, setRandomSeed] = useState(() => Math.random());
  const [fullResolutionScatter, setFullResolutionScatter] = useState(false);
  const [fitMode, setFitMode] = useState<RelationshipFitMode>("none");
  const [sync3DCells] = useState(true);
  const [shared3DCamera, setShared3DCamera] = useState<Mini3DCamera>(DEFAULT_MINI_3D_CAMERA);
  const [hideUninterestingCells, setHideUninterestingCells] = useState(false);
  const [pruneBoringAxes, setPruneBoringAxes] = useState(defaultPruneBoringAxes);
  const [hideWeakCorrelations, setHideWeakCorrelations] = useState(defaultHideWeakCorrelations);
  const [cellMenu, setCellMenu] = useState<CellMenuState | null>(null);
  const { openPlotBuilder } = useProvenanceContext();
  const offDiagPlotType = offDiagProp ?? localOffDiag;
  const diagPlotType = diagProp ?? localDiag;
  const handleOffDiagChange = onOffDiagPlotTypeChange ?? setLocalOffDiag;
  const handleDiagChange = onDiagPlotTypeChange ?? setLocalDiag;
  const previousZAxisKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const previousZAxisKey = previousZAxisKeyRef.current;
    if (zAxisKey === previousZAxisKey) return;
    previousZAxisKeyRef.current = zAxisKey;

    if (zAxisKey) {
      setCorrelationView("3d");
    } else if (!zAxisKey && offDiagPlotType === "scatter3d") {
      setCorrelationView("2d");
      handleOffDiagChange("scatter");
    } else if (!zAxisKey) {
      setCorrelationView("2d");
    }
  }, [handleOffDiagChange, offDiagPlotType, zAxisKey]);
  const zoomRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const resetBtnRef = useRef<HTMLButtonElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [expandedCell, setExpandedCell] = useState<{ row: number; col: number } | null>(null);
  const hoverRef = useRef<HoverInfo | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const targetColumnKey = useMemo(() => {
    if (zAxisKey && numericColumns.some((column) => column.key === zAxisKey)) return zAxisKey;
    return numericColumns.find((column) => column.role === "response")?.key;
  }, [numericColumns, zAxisKey]);

  const targetCorrelationScores = useMemo(() => {
    if (!targetColumnKey) return new Map<string, number>();
    const scores = rankColumnsByCorrelationTo(
      rows,
      numericColumns.map((column) => ({ field: column.key, headerName: column.header })),
      targetColumnKey,
    );
    return new Map(scores.map((score) => [score.field, score.score]));
  }, [numericColumns, rows, targetColumnKey]);

  const selectedColumns = useMemo(() => {
    const columnLimit = Math.max(2, maxColumns);
    let candidates = numericColumns;
    if (pruneBoringAxes || hideWeakCorrelations) {
      const baseFields = filterConstantColumns(
        rows,
        numericColumns.map((column) => ({ field: column.key, headerName: column.header })),
      ).map((column) => column.field);
      const baseFieldSet = new Set(baseFields);
      candidates = numericColumns.filter((column) => {
        if (column.key === targetColumnKey) return true;
        if (!baseFieldSet.has(column.key)) return false;
        const targetScore = targetCorrelationScores.get(column.key) ?? 0;
        if (hideWeakCorrelations && targetColumnKey && targetScore < weakCorrelationThreshold) return false;
        if (!pruneBoringAxes) return true;
        if (column.role === "response") return !targetColumnKey || targetScore >= weakCorrelationThreshold * 0.55;
        const weight = columnInterestingnessWeight(rows, column.key);
        return weight >= 0.18 || targetScore >= 0.24;
      });
      if (candidates.length < 2) {
        candidates = numericColumns.filter((column) => column.key === targetColumnKey || baseFieldSet.has(column.key)).slice(0, Math.min(2, numericColumns.length));
      }
    }

    if (candidates.length <= columnLimit) return candidates;

    const fields = selectCorrelogramColumns(
      rows,
      candidates.map((column) => ({ field: column.key, headerName: column.header })),
      selectionMode,
      columnLimit,
      randomSeed,
      { avoidFlatPairs: hideUninterestingCells || pruneBoringAxes },
    );
    const byKey = new Map(candidates.map((column) => [column.key, column]));
    let selectedFields = fields;
    if (targetColumnKey && byKey.has(targetColumnKey)) {
      const rankedToTarget = Array.from(targetCorrelationScores.entries())
        .filter(([field]) => byKey.has(field))
        .sort((left, right) => right[1] - left[1])
        .map(([field]) => field);
      selectedFields = Array.from(new Set([targetColumnKey, ...rankedToTarget, ...fields])).slice(0, columnLimit);
    }
    return selectedFields
      .map((field) => byKey.get(field))
      .filter((column): column is CorrelogramColumn => !!column);
  }, [
    hideUninterestingCells,
    hideWeakCorrelations,
    maxColumns,
    numericColumns,
    pruneBoringAxes,
    randomSeed,
    rows,
    selectionMode,
    targetColumnKey,
    targetCorrelationScores,
    weakCorrelationThreshold,
  ]);

  const resolvedZAxisLabel = useMemo(() => {
    if (zAxisLabel) return zAxisLabel;
    if (!zAxisKey) return undefined;
    return numericColumns.find((column) => column.key === zAxisKey)?.header ?? zAxisKey;
  }, [numericColumns, zAxisKey, zAxisLabel]);

  const responseDriverSummary = useMemo(
    () => summarizeResponseDrivers(rows, selectedColumns, zAxisKey, resolvedZAxisLabel),
    [rows, selectedColumns, zAxisKey, resolvedZAxisLabel],
  );

  useEffect(() => {
    if (!expandedCell) return;
    if (expandedCell.row >= selectedColumns.length || expandedCell.col >= selectedColumns.length) {
      setExpandedCell(null);
    }
  }, [expandedCell, selectedColumns.length]);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height });
    }
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => { observerRef.current?.disconnect(); }, []);

  const { reset, wasDrag, resetTransform } = useZoomPan(zoomRef, innerRef, resetBtnRef);

  const n = selectedColumns.length;
  const hiddenColumnCount = Math.max(0, numericColumns.length - n);

  const cellSize = useMemo(() => {
    if (n === 0 || containerSize.width === 0) return 0;
    const gridChromeW = LABEL_COL_W + INFO_PANEL_W + 40 + n;
    const availW = Math.max(n * 24, containerSize.width - gridChromeW);
    return Math.max(24, Math.floor(availW / n));
  }, [n, containerSize.width]);

  // New selections or resizes should start from a width-filled, scrollable
  // matrix rather than keeping an old zoom/pan transform around.
  useEffect(() => {
    if (cellSize === 0) return;
    const raf = requestAnimationFrame(() => resetTransform());
    return () => cancelAnimationFrame(raf);
  }, [cellSize, n, resetTransform]);

  const colRanges = useMemo<(ColRange | null)[]>(() =>
    selectedColumns.map(col => computeRange(extractNumbers(rows, col.key))),
  [rows, selectedColumns]);

  const colorByMode = useMemo<"numeric" | "categorical" | "none">(() => {
    if (!colorByKey) return "none";
    const sample = rows.slice(0, 50);
    const numericCount = sample.filter(r => typeof r[colorByKey] === "number" && Number.isFinite(r[colorByKey] as number)).length;
    return numericCount > sample.length * 0.5 ? "numeric" : "categorical";
  }, [rows, colorByKey]);

  const colorByValues = useMemo(() => {
    if (colorByMode !== "categorical" || !colorByKey) return undefined;
    return rows.map(r => String(r[colorByKey] ?? ""));
  }, [rows, colorByKey, colorByMode]);

  const colorByNumbers = useMemo(() => {
    if (colorByMode !== "numeric" || !colorByKey) return undefined;
    return rows.map(r => {
      const v = r[colorByKey];
      return typeof v === "number" && Number.isFinite(v) ? v : NaN;
    });
  }, [rows, colorByKey, colorByMode]);

  const groupColorByValues = useMemo(() => {
    if (!groups || groups.length <= 1) return undefined;
    const rowToGroup = new Map<number, string>();
    for (const g of groups) {
      for (const row of g.rows) {
        const idx = rows.indexOf(row);
        if (idx >= 0) rowToGroup.set(idx, g.key);
      }
    }
    return rows.map((_, i) => rowToGroup.get(i) ?? "");
  }, [groups, rows]);

  const groupColorMap = useMemo(() => {
    if (!groups || groups.length <= 1) return undefined;
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.key, g.color);
    return map;
  }, [groups]);

  const effectiveColorByValues = groupColorByValues ?? colorByValues;
  const effectiveColorByNumbers = groupColorByValues ? undefined : colorByNumbers;

  // Lazy correlogram: compute cell data on first access instead of eagerly
  // computing all n² cells upfront (which can generate millions of objects).
  const matrixData = useMemo(() => {
    type CellData = {
      pairs?: { x: number; y: number; idx: number }[];
      triples?: { x: number; y: number; z: number; idx: number }[];
      values?: number[];
      r?: number;
      rPartial?: number;
      multiR?: number;
      surfaceQuality?: number;
      relationship?: PairRelationshipScore;
    };
    const cache: Record<string, CellData> = {};

    const zValsAll = zAxisKey && correlationView === "2d" ? rows.map(r => {
      const v = r[zAxisKey];
      return typeof v === "number" && Number.isFinite(v) ? v : NaN;
    }) : null;

    function computeCell(key: string): CellData {
      const [si, sj] = key.split("-");
      const i = Number(si);
      const j = Number(sj);
      if (Number.isNaN(i) || Number.isNaN(j) || i >= n || j >= n) return {};

      if (i === j) {
        const colI = selectedColumns[i]!;
        const diag: CellData = { values: extractNumbers(rows, colI.key) };
        cache[key] = diag;
        return diag;
      }

      // Compute the canonical (min,max) pair and derive both orientations
      const ci = Math.min(i, j);
      const cj = Math.max(i, j);
      const canonKey = `${ci}-${cj}`;
      // If the canonical pair was already computed (via the mirror), return it
      if (canonKey in cache) return cache[key]!;

      const colI = selectedColumns[ci]!;
      const colJ = selectedColumns[cj]!;
      const pairs = extractPairs(rows, colI.key, colJ.key);
      const xVals = pairs.map(p => p.x);
      const yVals = pairs.map(p => p.y);
      const corr = pearsonCorrelation(xVals, yVals);
      const relationship = scoreAlignedRelationship(xVals, yVals);

      let triples: { x: number; y: number; z: number; idx: number }[] | undefined;
      let triplesSwapped: { x: number; y: number; z: number; idx: number }[] | undefined;
      let rPartial: number | undefined;
      let multiR: number | undefined;
      if (zAxisKey && zValsAll) {
        triples = extractTriples(rows, colI.key, colJ.key, zAxisKey);
        triplesSwapped = triples.map(t => ({ x: t.y, y: t.x, z: t.z, idx: t.idx }));
        const triX = triples.map(t => t.x);
        const triY = triples.map(t => t.y);
        const triZ = triples.map(t => t.z);
        rPartial = partialCorrelation(triX, triY, triZ);
        multiR = multipleCorrelationR(triX, triY, triZ);
      }

      let surfaceQuality: number | undefined;
      if (multiR != null && rPartial != null && Number.isFinite(multiR) && Number.isFinite(rPartial)) {
        const partialShift = Math.abs(Math.abs(corr) - Math.abs(rPartial));
        surfaceQuality = 0.6 * multiR + 0.4 * partialShift;
      }

      const swapped = pairs.map(p => ({ x: p.y, y: p.x, idx: p.idx }));
      // Cell (ci,cj): X = column cj, Y = row ci → pairs are swapped
      cache[`${ci}-${cj}`] = { pairs: swapped, triples: triplesSwapped, r: corr, rPartial, multiR, surfaceQuality, relationship };
      cache[`${cj}-${ci}`] = { pairs, triples, r: corr, rPartial, multiR, surfaceQuality, relationship };
      return cache[key]!;
    }

    return new Proxy(cache, {
      get(target, prop) {
        if (typeof prop !== "string") return Reflect.get(target, prop);
        if (!(prop in target)) computeCell(prop);
        return target[prop];
      },
    });
  }, [rows, selectedColumns, n, zAxisKey, correlationView]);

  // Whether to enable lazy rendering for off-screen cells (many cells)
  const useLazy = n * n > LAZY_THRESHOLD;

  // Auto-hexbin: when N > 2000 and user selected "scatter", use hexbin for
  // grid cells unless the user explicitly requests all points.
  const autoHexbin = rows.length > 2000 && offDiagPlotType === "scatter" && !fullResolutionScatter;

  // Whether to use canvas scatter (N > 200) instead of SVG Recharts
  const useCanvasScatter = rows.length > 200 || fullResolutionScatter;

  const handleCellClick = useCallback((i: number, j: number) => {
    if (wasDrag()) return;
    setCellMenu(null);
    setExpandedCell({ row: i, col: j });
  }, [wasDrag]);

  const getPlotContext = useCallback((i: number, j: number): CorrelogramPointContext | undefined => {
    if (i === j) return undefined;
    const rowCol = selectedColumns[i];
    const colCol = selectedColumns[j];
    if (!rowCol || !colCol) return undefined;
    const is3D = offDiagPlotType === "scatter3d" && !!zAxisKey;
    return {
      xField: colCol.key,
      yField: rowCol.key,
      zField: is3D ? zAxisKey : undefined,
      xLabel: colCol.header,
      yLabel: rowCol.header,
      zLabel: is3D ? zAxisKey : undefined,
    };
  }, [offDiagPlotType, selectedColumns, zAxisKey]);

  const openCellInPlotBuilder = useCallback((i: number, j: number) => {
    const context = getPlotContext(i, j);
    if (!context) return;
    openPlotBuilder(context.xField, context.yField, context.zField);
    setCellMenu(null);
  }, [getPlotContext, openPlotBuilder]);

  const handleGridContextMenu = useCallback((e: React.MouseEvent) => {
    const hover = hoverRef.current;
    if (!hover) return;
    e.preventDefault();
    e.stopPropagation();
    if (hover.row === hover.col) {
      setCellMenu({ x: e.clientX, y: e.clientY, row: hover.row, col: hover.col });
      return;
    }
    const context = getPlotContext(hover.row, hover.col);
    if (hover.point && onPointContextMenu) {
      onPointContextMenu(hover.point.idx, e, context);
      return;
    }
    setCellMenu({ x: e.clientX, y: e.clientY, row: hover.row, col: hover.col });
  }, [getPlotContext, onPointContextMenu]);

  const handleCellContextMenu = useCallback((e: React.MouseEvent, i: number, j: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (i !== j) {
      const context = getPlotContext(i, j);
      const pt = hoverRef.current?.row === i && hoverRef.current?.col === j
        ? hoverRef.current.point
        : undefined;
      if (pt && onPointContextMenu) {
        onPointContextMenu(pt.idx, e, context);
        return;
      }
    }
    setCellMenu({ x: e.clientX, y: e.clientY, row: i, col: j });
  }, [getPlotContext, onPointContextMenu]);

  if (n === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--gray-9, #6b7280)", flexDirection: "column", gap: 8, padding: 40 }}>
        <div style={{ fontSize: 18 }}>No numeric columns to plot</div>
        <div style={{ fontSize: 13 }}>Select at least two numeric fields above.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 12px", fontSize: 11, color: "var(--gray-9, #6b7280)", flexShrink: 0, flexWrap: "wrap" }}>
        <span>{n} of {numericColumns.length} variables{hiddenColumnCount ? ` · ${hiddenColumnCount} hidden` : ""}</span>
        <span>&middot;</span>
        <span>{rows.length} data point{rows.length !== 1 ? "s" : ""}</span>
        {groups && groups.length > 1 && (
          <>
            <span>&middot;</span>
            <span>{groups.length} groups</span>
          </>
        )}
        <span>&middot;</span>
        <span style={{ fontFamily: "var(--font-mono, monospace)" }}>r = Pearson correlation</span>
        {zAxisKey && (
          <>
            <span>&middot;</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)", color: "var(--blue-11)" }}>
              Z = {resolvedZAxisLabel ?? zAxisKey} &middot; r<sub>p</sub> = partial correlation
            </span>
          </>
        )}
        {responseDriverSummary && (
          <>
            <span>&middot;</span>
            <span
              title={responseDriverSummary.title}
              style={{
                color: responseDriverSummary.hasModelConditioned ? "var(--amber-11, #f59e0b)" : "var(--gray-11, #9ca3af)",
                maxWidth: 680,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {responseDriverSummary.text}
            </span>
          </>
        )}
        <span>{correlationView === "3d" ? "click a surface to inspect; drag to tilt, wheel to zoom" : "scroll to zoom, drag to pan, click to enlarge"}</span>
        <span style={{ marginLeft: "auto" }} />
        {numericColumns.length > maxColumns && (
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }} aria-label="Column selection mode">
            <span style={{ marginRight: 2 }}>Columns:</span>
            {SELECTION_MODES.map((mode) => {
              const active = selectionMode === mode.key;
              return (
                <button
                  key={mode.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSelectionMode(mode.key);
                    if (mode.key === "random") setRandomSeed(Math.random());
                  }}
                  style={{
                    padding: "2px 7px",
                    borderRadius: 5,
                    border: `1px solid ${active ? "var(--accent-8, #6366f1)" : "var(--gray-a5)"}`,
                    background: active ? "var(--accent-a4, rgba(99,102,241,0.2))" : "var(--gray-a2, rgba(255,255,255,0.04))",
                    color: active ? "var(--accent-11, #a5b4fc)" : "var(--gray-11, #9ca3af)",
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                  }}
                >
                  {mode.label}
                </button>
              );
            })}
            {selectionMode === "random" && (
              <button
                type="button"
                aria-label="Reroll random columns"
                onClick={() => setRandomSeed(Math.random())}
                style={{
                  padding: "2px 7px",
                  borderRadius: 5,
                  border: "1px solid var(--gray-a5)",
                  background: "var(--gray-a2, rgba(255,255,255,0.04))",
                  color: "var(--gray-11, #9ca3af)",
                  fontSize: 11,
                  cursor: "pointer",
                }}
              >
                Reroll
              </button>
            )}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 3 }} aria-label="Axis pruning controls">
          {([
            {
              key: "axes",
              label: "Auto axes",
              active: pruneBoringAxes,
              title: "Hide constant or repeated control axes unless they correlate with the selected output.",
              onClick: () => setPruneBoringAxes((value) => !value),
            },
            {
              key: "weak",
              label: "Hide weak",
              active: hideWeakCorrelations,
              title: `Hide fields with |r| below ${weakCorrelationThreshold.toFixed(2)} against the selected output.`,
              onClick: () => setHideWeakCorrelations((value) => !value),
            },
          ] as const).map((control) => (
            <button
              key={control.key}
              type="button"
              aria-pressed={control.active}
              onClick={control.onClick}
              title={control.title}
              style={{
                padding: "2px 7px",
                borderRadius: 5,
                border: `1px solid ${control.active ? "var(--accent-8, #6366f1)" : "var(--gray-a5)"}`,
                background: control.active ? "var(--accent-a4, rgba(99,102,241,0.2))" : "var(--gray-a2, rgba(255,255,255,0.04))",
                color: control.active ? "var(--accent-11, #a5b4fc)" : "var(--gray-11, #9ca3af)",
                fontSize: 11,
                fontWeight: control.active ? 700 : 500,
                cursor: "pointer",
              }}
            >
              {control.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }} aria-label="Correlation view">
          {(["2d", "3d"] as const).map((mode) => {
            const active = correlationView === mode;
            const disabled = mode === "3d" && !zAxisKey;
            return (
              <button
                key={mode}
                type="button"
                aria-pressed={active}
                disabled={disabled}
                onClick={() => {
                  setCorrelationView(mode);
                  if (mode === "2d" && offDiagPlotType === "scatter3d") handleOffDiagChange("scatter");
                }}
                title={mode === "3d" && !zAxisKey ? "Set a Z axis to enable 3D correlations" : undefined}
                style={{
                  padding: "2px 7px",
                  borderRadius: 5,
                  border: `1px solid ${active ? "var(--accent-8, #6366f1)" : "var(--gray-a5)"}`,
                  background: active ? "var(--accent-a4, rgba(99,102,241,0.2))" : "var(--gray-a2, rgba(255,255,255,0.04))",
                  color: disabled ? "var(--gray-8, #4b5563)" : active ? "var(--accent-11, #a5b4fc)" : "var(--gray-11, #9ca3af)",
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {mode === "2d" ? "2D correlations" : "3D correlations"}
              </button>
            );
          })}
        </div>
        {correlationView === "2d" && (
          <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span>Plot:</span>
          <select
            value={offDiagPlotType === "scatter3d" ? "scatter" : offDiagPlotType}
            onChange={(e) => handleOffDiagChange(e.target.value as OffDiagPlotType)}
            style={{
              padding: "1px 4px", border: "1px solid var(--gray-a5)", borderRadius: 3,
              background: "var(--color-surface)", color: "var(--gray-12)", fontSize: 11,
            }}
          >
            <option value="scatter">Scatter</option>
            <option value="line">Line</option>
            <option value="hexbin">Hexbin</option>
          </select>
          </label>
        )}
        {correlationView === "2d" && offDiagPlotType === "scatter" && (
          <label
            title="No downsampling; render every row in every scatter cell."
            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={fullResolutionScatter}
              onChange={(event) => setFullResolutionScatter(event.currentTarget.checked)}
            />
            <span>Full points</span>
          </label>
        )}
        {correlationView === "2d" && (
          <label
            title="Fit overlays are computed only for the enlarged cell view."
            style={{ display: "flex", alignItems: "center", gap: 3 }}
          >
            <span>Fit:</span>
            <select
              value={fitMode}
              onChange={(e) => setFitMode(e.target.value as RelationshipFitMode)}
              style={{
                padding: "1px 4px", border: "1px solid var(--gray-a5)", borderRadius: 3,
                background: "var(--color-surface)", color: "var(--gray-12)", fontSize: 11,
              }}
            >
              {FIT_MODES.map((mode) => (
                <option key={mode.key} value={mode.key}>{mode.short}</option>
              ))}
            </select>
          </label>
        )}
        {correlationView === "2d" && (
          <label
            title="Replace repeated/flat-axis columns with alternatives that produce non-flat pairings."
            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={hideUninterestingCells}
              onChange={(event) => setHideUninterestingCells(event.currentTarget.checked)}
            />
            <span>Hide flat</span>
          </label>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span>Diag:</span>
          <select
            value={diagPlotType}
            onChange={(e) => handleDiagChange(e.target.value as DiagPlotType)}
            style={{
              padding: "1px 4px", border: "1px solid var(--gray-a5)", borderRadius: 3,
              background: "var(--color-surface)", color: "var(--gray-12)", fontSize: 11,
            }}
          >
            <option value="histogram">Histogram</option>
            <option value="boxplot">Box Plot</option>
            <option value="violin">Violin/KDE</option>
          </select>
        </label>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {correlationView === "3d" ? (
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, padding: "8px 12px" }}>
            <SurfaceCorrelogram
              rows={rows}
              numericColumns={selectedColumns}
              zAxisKey={zAxisKey}
              zAxisLabel={resolvedZAxisLabel}
              zValueResolver={zValueResolver}
              selectedRowIdx={selectedRowIdx}
              onPointClick={onPointClick}
            />
          </div>
        ) : (
        <div
          ref={zoomRef}
          onContextMenu={handleGridContextMenu}
          style={{ flex: 1, overflow: "auto", position: "relative", cursor: "default", touchAction: "auto", userSelect: "none" }}
        >
          <button
            ref={resetBtnRef}
            onClick={(e) => { e.stopPropagation(); reset(); }}
            style={{
              display: "none",
              position: "absolute", top: 8, right: 8, zIndex: 10,
              padding: "4px 10px", borderRadius: 6,
              background: "var(--gray-a3)", border: "1px solid var(--gray-a5)",
              color: "var(--gray-11)", fontSize: 11, cursor: "pointer",
            }}
          >
            Reset zoom
          </button>

          <div ref={innerRef} style={{ transformOrigin: "0 0", padding: "8px 12px" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: `${LABEL_COL_W}px repeat(${n}, ${cellSize}px)`,
              gridTemplateRows: `${HEADER_ROW_H}px repeat(${n}, ${cellSize}px) ${TICK_ROW_H}px`,
              gap: 1,
              width: "fit-content",
            }}>
              <div />

              {selectedColumns.map((col, j) => (
                <div key={`col-header-${j}`} style={{
                  display: "flex", alignItems: "flex-end", justifyContent: "center",
                  fontSize: 9, fontWeight: 600,
                  color: "var(--gray-11, #9ca3af)", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", padding: "0 1px",
                }} title={col.key}>
                  {col.header}
                </div>
              ))}

              {selectedColumns.map((rowCol, i) => (
                <Fragment key={`row-${i}`}>
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center",
                    paddingRight: 4, overflow: "hidden",
                  }} title={rowCol.key}>
                    <span style={{
                      fontSize: 9, fontWeight: 600, lineHeight: 1.1,
                      color: "var(--gray-11, #9ca3af)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      maxWidth: LABEL_COL_W - 4, textAlign: "right",
                    }}>
                      {rowCol.header}
                    </span>
                    {colRanges[i] && (
                      <span style={{ fontSize: 7, color: "var(--gray-8, #4b5563)", fontFamily: "var(--font-mono, monospace)", lineHeight: 1, marginTop: 1 }}>
                        {shortLabel(colRanges[i]!.min)}&ndash;{shortLabel(colRanges[i]!.max)}
                      </span>
                    )}
                  </div>

                  {selectedColumns.map((colCol, j) => {
                    const cellData = matrixData[`${i}-${j}`];
                    if (i === j) {
                      const diagContent = (
                        <div
                          key={`cell-${i}-${j}`}
                          onClick={() => handleCellClick(i, j)}
                          onContextMenu={(e) => handleCellContextMenu(e, i, j)}
                          onMouseEnter={() => { hoverRef.current = { row: i, col: j }; }}
                          onMouseLeave={() => { hoverRef.current = null; }}
                          title={`${rowCol.header} distribution`}
                          style={{ width: cellSize, height: cellSize, background: "var(--accent-a2, rgba(99,102,241,0.03))", cursor: "pointer" }}
                        >
                          {diagPlotType === "boxplot"
                            ? <BoxPlotCellGrid values={cellData?.values ?? []} size={cellSize} />
                            : diagPlotType === "violin"
                            ? <ViolinCellGrid values={cellData?.values ?? []} size={cellSize} />
                            : <HistogramCellGrid values={cellData?.values ?? []} size={cellSize} label={rowCol.header} />}
                        </div>
                      );
                      return useLazy
                        ? <LazyCell key={`cell-${i}-${j}`} size={cellSize}>{diagContent}</LazyCell>
                        : <Fragment key={`cell-${i}-${j}`}>{diagContent}</Fragment>;
                    }
                    const rVal = cellData?.r;
                    const rStr = Number.isFinite(rVal) ? `  (r = ${rVal!.toFixed(3)})` : "";

                    // Determine which scatter renderer to use for this cell
                    const renderScatter = () => {
                      const pairs = cellData?.pairs ?? [];
                      // Auto-hexbin: N > 2000 and user hasn't explicitly chosen a type
                      if (autoHexbin) {
                        return <HexbinCellGrid data={pairs} size={cellSize} />;
                      }
                      // Canvas scatter for large datasets
                      if (useCanvasScatter) {
                        return (
                          <CanvasScatterCell
                            data={pairs}
                            size={cellSize}
                            row={i}
                            col={j}
                            fullResolution={fullResolutionScatter}
                            colorByValues={effectiveColorByValues}
                            colorByNumbers={effectiveColorByNumbers}
                            onPointClick={onPointClick}
                            onHoverPoint={hoverRef}
                            groupColorMap={groupColorMap}
                          />
                        );
                      }
                      // SVG Recharts for small datasets
                      return (
                        <ScatterCellGrid
                          data={pairs}
                          size={cellSize}
                          r={cellData?.r ?? NaN}
                          colorByValues={effectiveColorByValues}
                          colorByNumbers={effectiveColorByNumbers}
                          onPointClick={onPointClick}
                          onHoverPoint={hoverRef}
                          groupColorMap={groupColorMap}
                        />
                      );
                    };

                    const offDiagContent = (
                      <div
                        key={`cell-${i}-${j}`}
                        onClick={() => handleCellClick(i, j)}
                        onContextMenu={(e) => handleCellContextMenu(e, i, j)}
                        onMouseEnter={() => { hoverRef.current = { row: i, col: j }; }}
                        onMouseLeave={() => { hoverRef.current = null; }}
                        title={`${colCol.header} vs ${rowCol.header}${rStr}`}
                        style={{
                          width: cellSize,
                          height: cellSize,
                          cursor: "pointer",
                          position: "relative",
                          background: cellData?.relationship?.uninteresting ? "rgba(71, 85, 105, 0.08)" : undefined,
                        }}
                      >
                        {offDiagPlotType === "scatter3d" && cellData?.triples
                          ? <Scatter3DCellGrid
                              data={cellData.triples}
                              size={cellSize}
                              colorByValues={effectiveColorByValues}
                              colorByNumbers={effectiveColorByNumbers}
                              groupColorMap={groupColorMap}
                              camera={shared3DCamera}
                              syncCamera={sync3DCells}
                              onCameraChange={setShared3DCamera}
                            />
                          : offDiagPlotType === "line"
                          ? <LineCellGrid data={cellData?.pairs ?? []} size={cellSize} colorByValues={effectiveColorByValues} groupColorMap={groupColorMap} />
                          : offDiagPlotType === "hexbin"
                          ? <HexbinCellGrid data={cellData?.pairs ?? []} size={cellSize} />
                          : renderScatter()}
                        <RelationshipAtlasBadges relationship={cellData?.relationship} cellSize={cellSize} />
                        {offDiagPlotType === "scatter" && !autoHexbin && selectedRowIdx != null && cellData?.pairs && (
                          <SelectionDot data={cellData.pairs} size={cellSize} selectedRowIdx={selectedRowIdx} />
                        )}
                        {/* Surface quality badge for 3D mode */}
                        {offDiagPlotType === "scatter3d" && cellData?.surfaceQuality != null && cellSize >= 48 && (
                          <SurfaceQualityBadge
                            surfaceQuality={cellData.surfaceQuality}
                            rPartial={cellData.rPartial}
                            r={cellData.r ?? 0}
                            cellSize={cellSize}
                          />
                        )}
                      </div>
                    );

                    return useLazy
                      ? <LazyCell key={`cell-${i}-${j}`} size={cellSize}>{offDiagContent}</LazyCell>
                      : <Fragment key={`cell-${i}-${j}`}>{offDiagContent}</Fragment>;
                  })}
                </Fragment>
              ))}

              <div />
              {selectedColumns.map((_, j) => (
                <BottomTickCell key={`btick-${j}`} range={colRanges[j] ?? null} size={cellSize} />
              ))}
            </div>
          </div>

          {expandedCell && (
            <ExpandedCellOverlay
              row={expandedCell.row}
              col={expandedCell.col}
              numericColumns={selectedColumns}
              matrixData={matrixData}
              containerSize={containerSize}
              colorByValues={effectiveColorByValues}
              colorByNumbers={effectiveColorByNumbers}
              groupColorMap={groupColorMap}
              zAxisKey={zAxisKey}
              offDiagPlotType={offDiagPlotType}
              diagPlotType={diagPlotType}
              fitMode={fitMode}
              onClose={() => setExpandedCell(null)}
              onPointClick={onPointClick}
              onPointContextMenu={onPointContextMenu}
              selectedRowIdx={selectedRowIdx}
            />
          )}
          {cellMenu && selectedColumns[cellMenu.row] && selectedColumns[cellMenu.col] && (
            <CorrelogramCellContextMenu
              menu={cellMenu}
              rowCol={selectedColumns[cellMenu.row]!}
              colCol={selectedColumns[cellMenu.col]!}
              is3D={cellMenu.row !== cellMenu.col && offDiagPlotType === "scatter3d" && !!zAxisKey}
              canOpenPlotBuilder={cellMenu.row !== cellMenu.col}
              onOpenPlotBuilder={() => {
                if (cellMenu.row !== cellMenu.col) openCellInPlotBuilder(cellMenu.row, cellMenu.col);
                else {
                  setExpandedCell({ row: cellMenu.row, col: cellMenu.col });
                  setCellMenu(null);
                }
              }}
              onOpenInteractive={() => {
                setExpandedCell({ row: cellMenu.row, col: cellMenu.col });
                setCellMenu(null);
              }}
              onClose={() => setCellMenu(null)}
            />
          )}
        </div>
        )}

        {/* Hover info panel */}
        {correlationView === "2d" && (
          <HoverInfoPanel hoverRef={hoverRef} numericColumns={selectedColumns} matrixData={matrixData} rows={rows} zAxisKey={zAxisKey} />
        )}

        {/* Group legend — right sidebar */}
        {groups && groups.length > 1 && (
          <div style={{
            display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px",
            fontSize: 11, flexShrink: 0, overflowY: "auto", maxWidth: 220, minWidth: 120,
            borderLeft: "1px solid var(--gray-a4)",
          }}>
            {groups.map((g) => (
              <span key={g.key} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: g.color, flexShrink: 0, marginTop: 2 }} />
                <span style={{ color: "var(--gray-11, #9ca3af)", lineHeight: 1.3, wordBreak: "break-word" }}>{g.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Correlogram;
